import {
  CHAINS,
  HOME_CHAIN_ID,
  buildDID,
  chainForDID,
  getChain,
  listChains,
  parseDID,
  requireEnabledChain
} from "./chunk-ZNU572U7.mjs";
import {
  MarketplaceOperations,
  TYPED_DATA_FIELDS,
  buildClaimReceiptTypedData,
  buildConfirmReceiptTypedData,
  buildDisputeReceiptTypedData,
  buildHireOrderTypedData,
  buildResolveOrderTypedData,
  domainFromConfig,
  encodeDID,
  encodeType,
  hashDomain,
  hashStruct,
  hashTypedData,
  hashTypedDataHex,
  httpDelete,
  httpGet,
  httpPost,
  httpPut
} from "./chunk-MMSTBIXG.mjs";
import {
  computeDescriptorDiff,
  computeDiff,
  evalPath
} from "./chunk-DSF3Q6B5.mjs";

// src/crypto.ts
import * as ed from "@noble/ed25519";
import * as secp from "@noble/secp256k1";
var BASE58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
function base58Encode(data) {
  if (data.length === 0) return "";
  let leadingZeros = 0;
  for (const b of data) {
    if (b !== 0) break;
    leadingZeros++;
  }
  const size = Math.ceil(data.length * 138 / 100) + 1;
  const buf = new Uint8Array(size);
  let length = 0;
  for (const b of data) {
    let carry = b;
    for (let i = 0; i < length || carry !== 0; i++) {
      if (i < length) carry += 256 * buf[i];
      buf[i] = carry % 58;
      carry = Math.floor(carry / 58);
      if (i >= length) length = i + 1;
    }
  }
  let result = "";
  for (let i = 0; i < leadingZeros; i++) result += BASE58_ALPHABET[0];
  for (let i = length - 1; i >= 0; i--) result += BASE58_ALPHABET[buf[i]];
  return result;
}
function base58Decode(str) {
  if (str.length === 0) return new Uint8Array(0);
  const alphabetMap = /* @__PURE__ */ new Map();
  for (let i = 0; i < BASE58_ALPHABET.length; i++) {
    alphabetMap.set(BASE58_ALPHABET[i], i);
  }
  let leadingOnes = 0;
  for (const c of str) {
    if (c !== "1") break;
    leadingOnes++;
  }
  const size = Math.ceil(str.length * 733 / 1e3) + 1;
  const buf = new Uint8Array(size);
  let length = 0;
  for (const c of str) {
    const val = alphabetMap.get(c);
    if (val === void 0) throw new Error(`Invalid base58 character: ${c}`);
    let carry = val;
    for (let i = 0; i < length || carry !== 0; i++) {
      if (i < length) carry += 58 * buf[i];
      buf[i] = carry % 256;
      carry = Math.floor(carry / 256);
      if (i >= length) length = i + 1;
    }
  }
  const result = new Uint8Array(leadingOnes + length);
  for (let i = length - 1; i >= 0; i--) {
    result[leadingOnes + length - 1 - i] = buf[i];
  }
  return result;
}
function generateNonce(length = 32) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
}
function bytesToHex(bytes) {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}
function hexToBytes(hex) {
  if (hex.length % 2 !== 0) throw new Error("Invalid hex string");
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}
function base64UrlEncode(data) {
  const bytes = typeof data === "string" ? new TextEncoder().encode(data) : data;
  const base64 = btoa(String.fromCharCode(...bytes));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function base64UrlDecode(str) {
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4) base64 += "=";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
async function sha256(data) {
  const bytes = typeof data === "string" ? new TextEncoder().encode(data) : data;
  const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
  return new Uint8Array(hashBuffer);
}
async function ed25519Sign(privateKey, data) {
  if (privateKey.length !== 32 && privateKey.length !== 64) {
    throw new Error(`Ed25519 private key must be 32 or 64 bytes, got ${privateKey.length}`);
  }
  return ed.signAsync(data, privateKey.slice(0, 32));
}
async function ed25519Verify(publicKey, data, signature) {
  try {
    return await ed.verifyAsync(signature, data, publicKey);
  } catch {
    return false;
  }
}
async function generateEd25519KeyPair() {
  try {
    const keyPair = await crypto.subtle.generateKey("Ed25519", true, ["sign", "verify"]);
    const publicKeyBuffer = await crypto.subtle.exportKey("raw", keyPair.publicKey);
    const privateKeyBuffer = await crypto.subtle.exportKey("pkcs8", keyPair.privateKey);
    return {
      publicKey: new Uint8Array(publicKeyBuffer),
      privateKey: new Uint8Array(privateKeyBuffer)
    };
  } catch {
    const privateKey = new Uint8Array(64);
    crypto.getRandomValues(privateKey);
    const publicKeyHash = await sha256(new Uint8Array(privateKey.slice(0, 32)));
    return {
      publicKey: publicKeyHash,
      privateKey: new Uint8Array(privateKey)
    };
  }
}
function generateSecp256k1KeyPair() {
  const privateKey = secp.utils.randomPrivateKey();
  const publicKey = secp.getPublicKey(privateKey, true);
  return { publicKey, privateKey };
}
async function secp256k1Sign(privateKey, data) {
  const msgHash = await sha256(data);
  const signature = await secp.signAsync(msgHash, privateKey);
  return signature.toCompactRawBytes();
}
async function secp256k1Verify(publicKey, data, signature) {
  const msgHash = await sha256(data);
  return secp.verify(signature, msgHash, publicKey);
}

// src/capability.ts
function keyBytes(k) {
  if (typeof k !== "string") return k;
  const hex = k.trim().replace(/^0x/, "");
  return Uint8Array.from(hex.match(/.{2}/g) ?? [], (b) => parseInt(b, 16));
}
async function mintCapabilityToken(p) {
  const ttl = p.ttlSeconds ?? 60;
  if (ttl < 1 || ttl > 120) throw new Error("ttlSeconds must be between 1 and 120");
  if (p.amt !== void 0 && (!Number.isInteger(p.amt) || p.amt < 0)) {
    throw new Error("amt is an integer number of paise");
  }
  const now = Math.floor(Date.now() / 1e3);
  const header = { alg: "EdDSA", typ: "ibct+jwt", kid: p.keyId ?? `${p.agentDid}#key-1` };
  const payload = {
    iss: p.agentDid,
    aud: p.audience,
    mandate: p.mandate,
    act: p.act,
    jti: generateNonce(16),
    iat: now,
    exp: now + ttl
  };
  if (p.amt !== void 0) payload.amt = p.amt;
  if (p.payee) payload.payee = p.payee;
  if (p.offer) payload.offer = p.offer;
  if (p.mcc) payload.mcc = p.mcc;
  const signingInput = `${base64UrlEncode(JSON.stringify(header))}.${base64UrlEncode(JSON.stringify(payload))}`;
  const sig = await ed25519Sign(keyBytes(p.privateKey), new TextEncoder().encode(signingInput));
  return `${signingInput}.${base64UrlEncode(sig)}`;
}
var CapabilityTokens = class {
  constructor(config) {
    this.config = config;
  }
  /**
   * Redeem a token addressed to `audience` (a DID you control). Each token
   * is accepted once; a second redemption fails with 409.
   */
  async redeem(token, audience) {
    return httpPost(this.config, "/api/v1/ibcts/redeem", { token, audience });
  }
  /**
   * Merchant checkout: submit an agent's payment token addressed to your
   * merchant DID. IDA verifies it, records the decision and asks the
   * sponsor bank to debit; only the bank's signed result moves money.
   */
  async submitPayment(token, merchantDid, merchantOrderId) {
    return httpPost(this.config, "/api/v1/upi/payments", { token, merchantDid, merchantOrderId });
  }
  /** A signed policy decision record (the agent, the customer, their owners or an admin). */
  async getDecision(decisionId) {
    return httpGet(this.config, `/api/v1/upi/decisions/${encodeURIComponent(decisionId)}`);
  }
  /** Check a decision's signature and re-run it from its recorded inputs. */
  async replayDecision(decisionId) {
    return httpPost(this.config, `/api/v1/upi/decisions/${encodeURIComponent(decisionId)}/replay`, {});
  }
};

// src/upi.ts
var UPIOperations = class {
  constructor(config) {
    this.config = config;
  }
  /** The sponsor banks whose attestations mandates may rely on. */
  async sponsorBanks() {
    return httpGet(this.config, "/api/v1/upi/sponsor-banks");
  }
  /**
   * Sponsor bank: build an attestation to sign with the bank's organisation
   * key; complete it with the credentials API like any credential.
   */
  async prepareAttestation(req) {
    return httpPost(this.config, "/api/v1/upi/attestations/prepare", req);
  }
  /**
   * Attestations issued for an agent. An operator sees all of them; a
   * customer passes a DID they control and sees only theirs.
   */
  async listAttestations(agentDid, customerDid) {
    const q = new URLSearchParams({ agent: agentDid });
    if (customerDid) q.set("customer", customerDid);
    return httpGet(this.config, `/api/v1/upi/attestations?${q}`);
  }
  /** A customer's attestations with every agent, for a DID they control. */
  async listCustomerAttestations(customerDid) {
    const q = new URLSearchParams({ customer: customerDid });
    return httpGet(this.config, `/api/v1/upi/attestations?${q}`);
  }
  /** A payment and every change of its state. */
  async getPayment(paymentId) {
    return httpGet(this.config, `/api/v1/upi/payments/${encodeURIComponent(paymentId)}`);
  }
  /** Payments for one agent, merchant, customer or mandate you are a party to. */
  async listPayments(query) {
    const q = new URLSearchParams(Object.entries(query).filter(([, v]) => v));
    return httpGet(this.config, `/api/v1/upi/payments?${q}`);
  }
  /** Sponsor bank: push a signed debit result (M4). */
  async pushBankResult(signedResult) {
    return httpPost(this.config, "/api/v1/upi/bank/results", signedResult);
  }
  /** Development: answer a payment waiting in the mock bank's app, as the customer. */
  async mockResolvePayment(paymentId, approve) {
    return httpPost(this.config, `/api/v1/upi/mock-bank/payments/${encodeURIComponent(paymentId)}/resolve`, { approve });
  }
  /** Operator: propose a mandate to a customer, with its purpose. */
  async proposeMandate(req) {
    return httpPost(this.config, "/api/v1/upi/mandates/proposals", req);
  }
  /** Proposals to a customer, or from an agent, you control. */
  async listProposals(query) {
    const q = new URLSearchParams(Object.entries(query).filter(([, v]) => v));
    return httpGet(this.config, `/api/v1/upi/mandates/proposals?${q}`);
  }
  /** Customer: the mandate a proposal asks you to sign. */
  async prepareConsent(proposalId, verificationMethod) {
    return httpPost(this.config, `/api/v1/upi/mandates/proposals/${encodeURIComponent(proposalId)}/consent/prepare`, { verificationMethod });
  }
  /** Customer: your signature over the prepared mandate issues it. */
  async completeConsent(proposalId, credentialId, proofValue) {
    return httpPost(this.config, `/api/v1/upi/mandates/proposals/${encodeURIComponent(proposalId)}/consent`, { credentialId, proofValue });
  }
  /** Customer: refuse a proposal. */
  async declineProposal(proposalId) {
    return httpPost(this.config, `/api/v1/upi/mandates/proposals/${encodeURIComponent(proposalId)}/decline`, {});
  }
  /** Operator: withdraw a proposal nobody has answered. */
  async withdrawProposal(proposalId) {
    return httpPost(this.config, `/api/v1/upi/mandates/proposals/${encodeURIComponent(proposalId)}/withdraw`, {});
  }
  /** Mandates a customer granted, or an agent received, with their status and spending. */
  async listMandates(query) {
    const q = new URLSearchParams(Object.entries(query).filter(([, v]) => v));
    return httpGet(this.config, `/api/v1/upi/mandates?${q}`);
  }
  async getMandate(mandateId) {
    return httpGet(this.config, `/api/v1/upi/mandates/${encodeURIComponent(mandateId)}`);
  }
  /** Customer: a narrower replacement for a live mandate, to sign. It may only tighten. */
  async prepareNarrow(mandateId, constraints, validUntil, verificationMethod) {
    return httpPost(this.config, `/api/v1/upi/mandates/${encodeURIComponent(mandateId)}/narrow/prepare`, { constraints, validUntil, verificationMethod });
  }
  /** Customer: the signed narrower mandate replaces the live one and keeps its spending. */
  async completeNarrow(mandateId, credentialId, proofValue) {
    return httpPost(this.config, `/api/v1/upi/mandates/${encodeURIComponent(mandateId)}/narrow`, { credentialId, proofValue });
  }
  /** Development: set up an instrument at the mock bank, as the customer. */
  async mockAttest(req) {
    return httpPost(this.config, "/api/v1/upi/mock-bank/attestations", req);
  }
  /** Development: remove an instrument at the mock bank, as the customer. */
  async mockRemoveInstrument(attestationId, customerDid) {
    await httpPost(this.config, `/api/v1/upi/mock-bank/attestations/${encodeURIComponent(attestationId)}/remove`, { customerDid });
  }
};

// src/chain.ts
import { Contract, JsonRpcProvider, Wallet, toUtf8Bytes, toUtf8String, id as keccakId } from "ethers";
var DID_REGISTRY_ABI = [
  {
    inputs: [
      { internalType: "string", name: "did", type: "string" },
      { internalType: "bytes", name: "didDocument", type: "bytes" }
    ],
    name: "createDID",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "did", type: "string" },
      { internalType: "bytes", name: "newDocument", type: "bytes" }
    ],
    name: "updateDIDDocument",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "string", name: "did", type: "string" }],
    name: "deactivateDID",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "did", type: "string" },
      { internalType: "address", name: "newOwner", type: "address" }
    ],
    name: "rotateKey",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "did", type: "string" },
      { internalType: "bytes32", name: "delegateType", type: "bytes32" },
      { internalType: "address", name: "delegate", type: "address" },
      { internalType: "uint256", name: "validity", type: "uint256" }
    ],
    name: "addDelegate",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "did", type: "string" },
      { internalType: "bytes32", name: "delegateType", type: "bytes32" },
      { internalType: "address", name: "delegate", type: "address" }
    ],
    name: "revokeDelegate",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "did", type: "string" },
      { internalType: "bytes32", name: "name", type: "bytes32" },
      { internalType: "bytes", name: "value", type: "bytes" },
      { internalType: "uint256", name: "validity", type: "uint256" }
    ],
    name: "setAttribute",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "did", type: "string" },
      { internalType: "bytes32", name: "name", type: "bytes32" },
      { internalType: "bytes", name: "value", type: "bytes" }
    ],
    name: "revokeAttribute",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "string", name: "did", type: "string" }],
    name: "didExists",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "did", type: "string" },
      { internalType: "bytes32", name: "delegateType", type: "bytes32" },
      { internalType: "address", name: "delegate", type: "address" }
    ],
    name: "validDelegate",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "string", name: "did", type: "string" }],
    name: "resolveDID",
    outputs: [
      { internalType: "bytes", name: "didDocument", type: "bytes" },
      { internalType: "address", name: "owner", type: "address" },
      { internalType: "uint256", name: "created", type: "uint256" },
      { internalType: "uint256", name: "updated", type: "uint256" },
      { internalType: "bool", name: "deactivated", type: "bool" }
    ],
    stateMutability: "view",
    type: "function"
  }
];
var AGENT_TRUST_REGISTRY_ABI = [
  {
    inputs: [
      { internalType: "string", name: "agentDid", type: "string" },
      { internalType: "string", name: "operatorDid", type: "string" },
      { internalType: "bytes", name: "agentCard", type: "bytes" }
    ],
    name: "registerAgent",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "agentDid", type: "string" },
      { internalType: "bytes", name: "newCard", type: "bytes" }
    ],
    name: "updateAgentCard",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "agentDid", type: "string" },
      { internalType: "uint8", name: "newStatus", type: "uint8" }
    ],
    name: "updateAgentStatus",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "string", name: "agentDid", type: "string" }],
    name: "decommissionAgent",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "agentDid", type: "string" },
      { internalType: "int8", name: "signal", type: "int8" },
      { internalType: "string", name: "context", type: "string" }
    ],
    name: "submitTrustSignal",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "agentDid", type: "string" },
      { internalType: "uint8", name: "level", type: "uint8" }
    ],
    name: "setAutonomyLevel",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "string", name: "agentDid", type: "string" }],
    name: "agentExists",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "agentDid", type: "string" },
      { internalType: "string", name: "sponsorDid", type: "string" }
    ],
    name: "confirmSponsorship",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "string", name: "agentDid", type: "string" }],
    name: "revokeSponsorship",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "string", name: "agentDid", type: "string" }],
    name: "getSponsor",
    outputs: [
      { internalType: "string", name: "sponsorDid", type: "string" },
      { internalType: "address", name: "sponsor", type: "address" },
      { internalType: "bool", name: "confirmed", type: "bool" }
    ],
    stateMutability: "view",
    type: "function"
  }
];
var ZK_PROOF_VERIFIER_ABI = [
  {
    inputs: [
      { internalType: "bytes32", name: "proofHash", type: "bytes32" },
      { internalType: "string", name: "proverDID", type: "string" },
      { internalType: "string", name: "credentialId", type: "string" },
      { internalType: "bytes32", name: "predicateHash", type: "bytes32" }
    ],
    name: "submitProof",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "proofHash", type: "bytes32" }],
    name: "verifyProofOnChain",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "proofHash", type: "bytes32" }],
    name: "revokeProof",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  }
];
var SCHEMA_REGISTRY_ABI = [
  {
    inputs: [
      { internalType: "string", name: "schemaId", type: "string" },
      { internalType: "string", name: "name", type: "string" },
      { internalType: "string", name: "version", type: "string" },
      { internalType: "bytes", name: "schemaDefinition", type: "bytes" }
    ],
    name: "createSchema",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "schemaId", type: "string" },
      { internalType: "string", name: "newVersion", type: "string" },
      { internalType: "bytes", name: "newDefinition", type: "bytes" }
    ],
    name: "updateSchema",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  }
];
var REVOCATION_REGISTRY_ABI = [
  {
    inputs: [
      { internalType: "string", name: "issuerDid", type: "string" },
      { internalType: "string", name: "credentialId", type: "string" },
      { internalType: "string", name: "reason", type: "string" }
    ],
    name: "revokeCredential",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "issuerDid", type: "string" },
      { internalType: "string[]", name: "credentialIds", type: "string[]" },
      { internalType: "string", name: "reason", type: "string" }
    ],
    name: "batchRevoke",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "issuerDid", type: "string" },
      { internalType: "string", name: "credentialId", type: "string" }
    ],
    name: "isRevoked",
    outputs: [
      { internalType: "bool", name: "revoked", type: "bool" },
      { internalType: "string", name: "reason", type: "string" },
      { internalType: "uint256", name: "revokedAt", type: "uint256" },
      { internalType: "address", name: "revokedBy", type: "address" }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "string", name: "issuerDid", type: "string" },
      { internalType: "address", name: "account", type: "address" }
    ],
    name: "canRevoke",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  }
];
var AGENT_STATUS = {
  Registered: 0,
  Active: 1,
  Suspended: 2,
  Decommissioned: 3
};
var AUTONOMY_LEVEL = {
  Intern: 0,
  Junior: 1,
  Senior: 2,
  Principal: 3
};
function toBytes32(label) {
  return keccakId(label);
}
function encodeDocument(doc) {
  return toUtf8Bytes(JSON.stringify(doc));
}
function buildWalletDIDDocument(did, address, chainId) {
  return {
    "@context": ["https://www.w3.org/ns/did/v1"],
    id: did,
    verificationMethod: [
      {
        id: `${did}#key-1`,
        type: "EcdsaSecp256k1VerificationKey2019",
        controller: did,
        blockchainAccountId: `eip155:${chainId}:${address}`
      }
    ],
    authentication: [`${did}#key-1`]
  };
}
function resolveSigner(signer, chain) {
  if (typeof signer === "string") {
    const key = signer.startsWith("0x") ? signer : `0x${signer}`;
    const provider = new JsonRpcProvider(chain.rpcUrl, chain.chainId);
    return new Wallet(key, provider);
  }
  return signer;
}
async function createDIDOnChain(signer, chain, services) {
  const registry = requireAddress(chain.didRegistry, "didRegistry");
  const ethSigner = resolveSigner(signer, chain);
  const address = (await ethSigner.getAddress()).toLowerCase();
  const did = buildDID(chain.chainId, address);
  const contract = new Contract(registry, DID_REGISTRY_ABI, ethSigner);
  const exists = await readCall(chain, registry, "didExists", () => contract.didExists(did));
  if (exists) {
    throw new Error(`DID ${did} already registered on-chain for this wallet address.`);
  }
  const doc = buildWalletDIDDocument(did, address, chain.chainId);
  if (services && services.length > 0) {
    doc.service = services;
  }
  try {
    const tx = await contract.createDID(did, encodeDocument(doc));
    const receipt = await tx.wait();
    return {
      did,
      address,
      txHash: tx.hash,
      blockNumber: receipt?.blockNumber,
      document: doc
    };
  } catch (err) {
    throw describeTxError(err, chain, "createDID");
  }
}
function requireAddress(addr, field) {
  if (!addr) {
    throw new Error(
      `chain.${String(field)} is required for this self-sovereign operation. Set it on the IDAClient's chain config.`
    );
  }
  return addr;
}
function describeTxError(err, chain, method) {
  const e = err;
  const haystack = `${e?.message ?? ""} ${e?.shortMessage ?? ""} ${JSON.stringify(e?.info ?? e?.error ?? {})}`;
  const known = CHAINS[chain.chainId];
  const where = known ? `${known.name} (chain ${chain.chainId})` : `chain ${chain.chainId}`;
  if (haystack.includes("insufficient funds") || haystack.includes("enough funds")) {
    const faucet = known?.faucet ? ` Get test funds: ${known.faucet}` : "";
    return new Error(
      `${method} failed: the signing wallet has no gas on ${where}. Fund the wallet and retry.${faucet}`
    );
  }
  if (haystack.includes("revert")) {
    return new Error(`${method} reverted on ${where}: ${e?.shortMessage ?? e?.message ?? "contract rejected the call"}`);
  }
  return err instanceof Error ? err : new Error(String(err));
}
async function readCall(chain, address, method, run) {
  try {
    return await run();
  } catch (err) {
    const e = err;
    throw new Error(
      `${method} failed: no contract responded at ${address} on chain ${chain.chainId}. Check the registry address and rpcUrl point at the same network. (${e?.shortMessage ?? e?.message ?? String(err)})`
    );
  }
}
async function sendTx(signer, chain, address, abi, method, args) {
  const ethSigner = resolveSigner(signer, chain);
  const contract = new Contract(address, abi, ethSigner);
  try {
    const tx = await contract[method](...args);
    const receipt = await tx.wait();
    return { txHash: tx.hash, blockNumber: receipt?.blockNumber, chainId: chain.chainId };
  } catch (err) {
    throw describeTxError(err, chain, method);
  }
}
function updateDIDDocumentOnChain(signer, chain, did, newDocument) {
  return sendTx(signer, chain, chain.didRegistry, DID_REGISTRY_ABI, "updateDIDDocument", [did, encodeDocument(newDocument)]);
}
function deactivateDIDOnChain(signer, chain, did) {
  return sendTx(signer, chain, chain.didRegistry, DID_REGISTRY_ABI, "deactivateDID", [did]);
}
function rotateKeyOnChain(signer, chain, did, newOwner) {
  return sendTx(signer, chain, chain.didRegistry, DID_REGISTRY_ABI, "rotateKey", [did, newOwner]);
}
function addDelegateOnChain(signer, chain, did, delegateType, delegate, validitySeconds) {
  return sendTx(signer, chain, chain.didRegistry, DID_REGISTRY_ABI, "addDelegate", [did, toBytes32(delegateType), delegate, validitySeconds]);
}
function revokeDelegateOnChain(signer, chain, did, delegateType, delegate) {
  return sendTx(signer, chain, chain.didRegistry, DID_REGISTRY_ABI, "revokeDelegate", [did, toBytes32(delegateType), delegate]);
}
function setAttributeOnChain(signer, chain, did, name, value, validitySeconds) {
  return sendTx(signer, chain, chain.didRegistry, DID_REGISTRY_ABI, "setAttribute", [did, toBytes32(name), toUtf8Bytes(value), validitySeconds]);
}
function revokeAttributeOnChain(signer, chain, did, name, value) {
  return sendTx(signer, chain, chain.didRegistry, DID_REGISTRY_ABI, "revokeAttribute", [did, toBytes32(name), toUtf8Bytes(value)]);
}
function registerAgentOnChain(signer, chain, agentDid, operatorDid, agentCard) {
  const addr = requireAddress(chain.agentTrustRegistry, "agentTrustRegistry");
  return sendTx(signer, chain, addr, AGENT_TRUST_REGISTRY_ABI, "registerAgent", [agentDid, operatorDid, encodeDocument(agentCard)]);
}
function updateAgentCardOnChain(signer, chain, agentDid, newCard) {
  const addr = requireAddress(chain.agentTrustRegistry, "agentTrustRegistry");
  return sendTx(signer, chain, addr, AGENT_TRUST_REGISTRY_ABI, "updateAgentCard", [agentDid, encodeDocument(newCard)]);
}
function updateAgentStatusOnChain(signer, chain, agentDid, status) {
  const addr = requireAddress(chain.agentTrustRegistry, "agentTrustRegistry");
  return sendTx(signer, chain, addr, AGENT_TRUST_REGISTRY_ABI, "updateAgentStatus", [agentDid, AGENT_STATUS[status]]);
}
function decommissionAgentOnChain(signer, chain, agentDid) {
  const addr = requireAddress(chain.agentTrustRegistry, "agentTrustRegistry");
  return sendTx(signer, chain, addr, AGENT_TRUST_REGISTRY_ABI, "decommissionAgent", [agentDid]);
}
function submitTrustSignalOnChain(signer, chain, agentDid, signal, context) {
  const addr = requireAddress(chain.agentTrustRegistry, "agentTrustRegistry");
  return sendTx(signer, chain, addr, AGENT_TRUST_REGISTRY_ABI, "submitTrustSignal", [agentDid, signal, context]);
}
function setAutonomyLevelOnChain(signer, chain, agentDid, level) {
  const addr = requireAddress(chain.agentTrustRegistry, "agentTrustRegistry");
  return sendTx(signer, chain, addr, AGENT_TRUST_REGISTRY_ABI, "setAutonomyLevel", [agentDid, AUTONOMY_LEVEL[level]]);
}
function submitProofOnChain(signer, chain, proofHash, proverDID, credentialId, predicateHash) {
  const addr = requireAddress(chain.zkProofVerifier, "zkProofVerifier");
  return sendTx(signer, chain, addr, ZK_PROOF_VERIFIER_ABI, "submitProof", [proofHash, proverDID, credentialId, predicateHash]);
}
function verifyProofOnChain(signer, chain, proofHash) {
  const addr = requireAddress(chain.zkProofVerifier, "zkProofVerifier");
  return sendTx(signer, chain, addr, ZK_PROOF_VERIFIER_ABI, "verifyProofOnChain", [proofHash]);
}
function revokeProofOnChain(signer, chain, proofHash) {
  const addr = requireAddress(chain.zkProofVerifier, "zkProofVerifier");
  return sendTx(signer, chain, addr, ZK_PROOF_VERIFIER_ABI, "revokeProof", [proofHash]);
}
function createSchemaOnChain(signer, chain, schemaId, name, version, definition) {
  const addr = requireAddress(chain.schemaRegistry, "schemaRegistry");
  return sendTx(signer, chain, addr, SCHEMA_REGISTRY_ABI, "createSchema", [schemaId, name, version, encodeDocument(definition)]);
}
function updateSchemaOnChain(signer, chain, schemaId, newVersion, newDefinition) {
  const addr = requireAddress(chain.schemaRegistry, "schemaRegistry");
  return sendTx(signer, chain, addr, SCHEMA_REGISTRY_ABI, "updateSchema", [schemaId, newVersion, encodeDocument(newDefinition)]);
}
function revokeCredentialOnChain(signer, chain, issuerDid, credentialId, reason) {
  const addr = requireAddress(chain.revocationRegistry, "revocationRegistry");
  return sendTx(signer, chain, addr, REVOCATION_REGISTRY_ABI, "revokeCredential", [issuerDid, credentialId, reason]);
}
async function isRevokedOnChain(chain, issuerDid, credentialId) {
  const addr = requireAddress(chain.revocationRegistry, "revocationRegistry");
  const provider = new JsonRpcProvider(chain.rpcUrl, chain.chainId);
  const contract = new Contract(addr, REVOCATION_REGISTRY_ABI, provider);
  const [revoked, reason, revokedAt, revokedBy] = await contract.isRevoked(issuerDid, credentialId);
  return { revoked: Boolean(revoked), reason: String(reason), revokedAt: Number(revokedAt), revokedBy: String(revokedBy) };
}
function batchRevokeOnChain(signer, chain, issuerDid, credentialIds, reason) {
  const addr = requireAddress(chain.revocationRegistry, "revocationRegistry");
  return sendTx(signer, chain, addr, REVOCATION_REGISTRY_ABI, "batchRevoke", [issuerDid, credentialIds, reason]);
}
async function lookupDIDOnChain(chain, walletOrDid) {
  const s = walletOrDid.trim();
  let did;
  let target = chain;
  if (s.startsWith("did:")) {
    const parsed = parseDID(s);
    did = parsed.canonical;
    if (parsed.chainId !== void 0 && parsed.chainId !== chain.chainId) {
      const known = CHAINS[parsed.chainId];
      if (!known?.didRegistry) {
        throw new Error(`DID names chain ${parsed.chainId}, which has no deployed DIDRegistry in the registry.`);
      }
      target = known;
    }
  } else {
    const addr = (s.startsWith("0x") ? s : `0x${s}`).toLowerCase();
    did = buildDID(chain.chainId, addr);
  }
  const provider = new JsonRpcProvider(target.rpcUrl, target.chainId);
  const contract = new Contract(target.didRegistry, DID_REGISTRY_ABI, provider);
  const exists = await readCall(target, target.didRegistry, "didExists", () => contract.didExists(did));
  if (!exists) return { did, exists: false };
  const [documentBytes, owner, created, updated, deactivated] = await readCall(target, target.didRegistry, "resolveDID", () => contract.resolveDID(did));
  let document;
  try {
    document = JSON.parse(toUtf8String(documentBytes));
  } catch {
    document = void 0;
  }
  return {
    did,
    exists: true,
    owner: String(owner),
    deactivated: Boolean(deactivated),
    created: Number(created),
    updated: Number(updated),
    document
  };
}
function confirmSponsorshipOnChain(signer, chain, agentDid, sponsorDid) {
  const addr = requireAddress(chain.agentTrustRegistry, "agentTrustRegistry");
  return sendTx(signer, chain, addr, AGENT_TRUST_REGISTRY_ABI, "confirmSponsorship", [agentDid, sponsorDid]);
}
function revokeSponsorshipOnChain(signer, chain, agentDid) {
  const addr = requireAddress(chain.agentTrustRegistry, "agentTrustRegistry");
  return sendTx(signer, chain, addr, AGENT_TRUST_REGISTRY_ABI, "revokeSponsorship", [agentDid]);
}
async function getSponsorOnChain(chain, agentDid) {
  const addr = requireAddress(chain.agentTrustRegistry, "agentTrustRegistry");
  const provider = new JsonRpcProvider(chain.rpcUrl, chain.chainId);
  const contract = new Contract(addr, AGENT_TRUST_REGISTRY_ABI, provider);
  const [sponsorDid, sponsor, confirmed] = await readCall(chain, addr, "getSponsor", () => contract.getSponsor(agentDid));
  return { sponsorDid, sponsor: String(sponsor), confirmed: Boolean(confirmed) };
}

// src/did.ts
function requireChain(config, op, network) {
  if (network !== void 0) return requireEnabledChain(network);
  if (!config.chain) {
    throw new Error(
      `${op} with a signer needs a chain: pass { network: 'adi' | 'base-sepolia' | <chainId> } or set \`chain\` config on the IDAClient.`
    );
  }
  return config.chain;
}
var DIDOperations = class {
  constructor(config) {
    this.config = config;
  }
  // ========== Core DID Lifecycle ==========
  /**
   * Create a new DID.
   *
   * Two modes:
   *
   * 1. **Self-sovereign on-chain** — pass `options.signer` (a hex private key or
   *    an ethers Signer) and configure `chain` on the IDAClient. The SDK signs
   *    and submits DIDRegistry.createDID() locally, waits for the receipt, then
   *    syncs the record to the IDA API. The private key never leaves your
   *    process. This is the recommended, self-sovereign path.
   *
   * 2. **API-only (legacy)** — omit `signer`. The server generates a keypair and
   *    stores the DID in its database. NOT on-chain and NOT self-sovereign; the
   *    returned `keys.privateKey` was generated server-side. Kept for backward
   *    compatibility and quick testing only.
   */
  async createDID(options) {
    if (options?.signer) {
      const chain = requireChain(this.config, "createDID", options.network);
      const onchain = await createDIDOnChain(options.signer, chain, options.services);
      let synced = true;
      let syncError;
      try {
        await httpPost(this.config, "/api/v1/dids", {
          walletAddress: onchain.address,
          chainId: String(chain.chainId),
          txHash: onchain.txHash,
          services: options.services
        });
      } catch (err) {
        synced = false;
        syncError = err instanceof Error ? err.message : String(err);
        console.warn(
          `[@myida/sdk] ${onchain.did} was created on-chain (tx ${onchain.txHash}) but the off-chain record was not synced to the API: ${syncError}. It will not appear in the portal until re-synced. Use a user API key (Settings \u2192 API Keys), not the server API_KEY.`
        );
      }
      return {
        did: onchain.did,
        document: onchain.document,
        synced,
        syncError,
        onChain: {
          txHash: onchain.txHash,
          blockNumber: onchain.blockNumber,
          chainId: chain.chainId
        }
      };
    }
    const body = {};
    if (options?.keyType) {
      body.keyType = options.keyType === "ed25519" ? "Ed25519" : "Secp256k1";
    }
    if (options?.services) {
      body.services = options.services;
    }
    return httpPost(this.config, "/api/v1/dids", body);
  }
  /**
   * Resolve a did:adi DID to its DID Document (authenticated).
   */
  async resolveDID(did) {
    const encoded = encodeDID(did);
    const raw = await httpGet(
      this.config,
      `/api/v1/dids/${encoded}`
    );
    return raw.didDocument ?? raw;
  }
  /**
   * Resolve any DID (did:adi, did:key, did:web, did:ethr) via the universal resolver.
   * This is a public endpoint — no authentication required.
   */
  async resolveAny(did) {
    const encoded = encodeDID(did);
    return httpGet(this.config, `/api/v1/dids/resolve/${encoded}`);
  }
  /**
   * Update a DID Document with new services, verification methods, etc.
   *
   * Dual-mode: pass `opts.signer` to self-sovereignly submit
   * DIDRegistry.updateDIDDocument() on-chain (the signer must own the DID) and
   * receive the tx result; omit it for the legacy API path.
   */
  async updateDID(did, document, opts) {
    if (opts?.signer) {
      return updateDIDDocumentOnChain(opts.signer, requireChain(this.config, "updateDID", opts?.network), did, document);
    }
    const encoded = encodeDID(did);
    await httpPut(this.config, `/api/v1/dids/${encoded}`, document);
  }
  /**
   * Deactivate a DID, rendering it permanently inactive.
   *
   * Dual-mode: pass `opts.signer` to submit DIDRegistry.deactivateDID()
   * on-chain (signer must own the DID); omit it for the legacy API path.
   */
  async deactivateDID(did, opts) {
    if (opts?.signer) {
      return deactivateDIDOnChain(opts.signer, requireChain(this.config, "deactivateDID", opts?.network), did);
    }
    const encoded = encodeDID(did);
    await httpDelete(this.config, `/api/v1/dids/${encoded}`);
  }
  /**
   * Get the message a current authentication key must sign to authorise a key
   * rotation. It names the current document version, so each signed challenge
   * works for one rotation only.
   */
  async getRotationChallenge(did) {
    return httpGet(this.config, `/api/v1/dids/${encodeDID(did)}/rotate-key/challenge`);
  }
  /**
   * Rotate the key associated with a DID.
   *
   * API path: `currentKeyProof` is a current authentication key's signature
   * over the message from {@link getRotationChallenge} (base64url/base58/hex
   * for Ed25519, or a MetaMask personal_sign signature for a wallet DID).
   * Pass `opts.newPublicKeyMultibase` to keep the new private key yourself;
   * otherwise the server generates it and returns it once. Set
   * `opts.compromised` when the old key leaked, so nothing it signed verifies
   * any more.
   *
   * On-chain path: pass `opts.signer` AND `opts.newOwner` (a 0x address) to
   * submit DIDRegistry.rotateKey(), transferring ownership to `newOwner`.
   */
  async rotateKey(did, keyType, currentKeyProof, opts) {
    if (opts?.signer) {
      if (!opts.newOwner) {
        throw new Error("rotateKey with a signer requires opts.newOwner (the 0x address to transfer DID ownership to).");
      }
      return rotateKeyOnChain(opts.signer, requireChain(this.config, "rotateKey", opts?.network), did, opts.newOwner);
    }
    if (!currentKeyProof) {
      throw new Error("rotateKey requires currentKeyProof: sign the message from getRotationChallenge() with a current authentication key.");
    }
    const encoded = encodeDID(did);
    return httpPost(this.config, `/api/v1/dids/${encoded}/rotate-key`, {
      keyType: keyType || "Ed25519",
      currentKeyProof,
      ...opts?.newPublicKeyMultibase ? { newPublicKeyMultibase: opts.newPublicKeyMultibase } : {},
      ...opts?.compromised ? { compromised: true } : {},
      ...opts?.reason ? { reason: opts.reason } : {}
    });
  }
  /** List every recorded version of a DID document, newest first. */
  async listDIDVersions(did) {
    return httpGet(this.config, `/api/v1/dids/${encodeDID(did)}/versions`);
  }
  /**
   * Resolve a DID as it was at a version or a point in time (DID Resolution
   * versionId / versionTime).
   */
  async resolveDIDVersion(did, at) {
    const q = "versionId" in at ? `versionId=${encodeURIComponent(String(at.versionId))}` : `versionTime=${encodeURIComponent(at.versionTime instanceof Date ? at.versionTime.toISOString() : at.versionTime)}`;
    return httpGet(this.config, `/api/v1/dids/${encodeDID(did)}?${q}`);
  }
  // ========== Delegate Management (ERC-1056 inspired) ==========
  /**
   * Add a delegate for a DID with a specific type and validity period.
   * @param did - The DID to add a delegate to.
   * @param params - Delegate type ("veriKey" or "sigAuth"), delegate address, and validity in seconds.
   */
  async addDelegate(did, params, opts) {
    if (opts?.signer) {
      return addDelegateOnChain(opts.signer, requireChain(this.config, "addDelegate", opts?.network), did, params.delegateType, params.delegate, params.validity);
    }
    const encoded = encodeDID(did);
    return httpPost(this.config, `/api/v1/dids/${encoded}/delegates`, params);
  }
  /**
   * List active delegates for a DID (non-expired, non-revoked).
   */
  async listDelegates(did) {
    const encoded = encodeDID(did);
    return httpGet(this.config, `/api/v1/dids/${encoded}/delegates`);
  }
  /**
   * Revoke a delegate before its expiry.
   */
  async revokeDelegate(did, params, opts) {
    if (opts?.signer) {
      return revokeDelegateOnChain(opts.signer, requireChain(this.config, "revokeDelegate", opts?.network), did, params.delegateType, params.delegate);
    }
    const encoded = encodeDID(did);
    await httpDelete(this.config, `/api/v1/dids/${encoded}/delegates`, params);
  }
  // ========== Attribute Management (ERC-1056 inspired) ==========
  /**
   * Set an attribute on a DID with a validity period.
   * Attribute names follow the ERC-1056 convention:
   * - did/pub/<keyType>/<purpose>/<encoding> for public keys
   * - did/svc/<serviceType> for service endpoints
   */
  async setAttribute(did, params, opts) {
    if (opts?.signer) {
      return setAttributeOnChain(opts.signer, requireChain(this.config, "setAttribute", opts?.network), did, params.name, params.value, params.validity);
    }
    const encoded = encodeDID(did);
    return httpPost(this.config, `/api/v1/dids/${encoded}/attributes`, params);
  }
  /**
   * Revoke an attribute before its expiry.
   */
  async revokeAttribute(did, params, opts) {
    if (opts?.signer) {
      return revokeAttributeOnChain(opts.signer, requireChain(this.config, "revokeAttribute", opts?.network), did, params.name, params.value);
    }
    const encoded = encodeDID(did);
    await httpDelete(this.config, `/api/v1/dids/${encoded}/attributes`, params);
  }
};

// src/dataintegrity.ts
var DATA_INTEGRITY_PROOF = "DataIntegrityProof";
var CRYPTOSUITE_EDDSA_JCS = "eddsa-jcs-2022";
var CRYPTOSUITE_EIP191_JCS = "ida-eip191-jcs-2026";
function canonicalize(value) {
  if (value === null || typeof value === "boolean" || typeof value === "string") {
    return JSON.stringify(value);
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("JCS: NaN and Infinity are not valid JSON numbers");
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return "[" + value.map((v) => canonicalize(v === void 0 ? null : v)).join(",") + "]";
  }
  if (typeof value === "object") {
    const obj = value;
    const keys = Object.keys(obj).filter((k) => obj[k] !== void 0).sort();
    return "{" + keys.map((k) => JSON.stringify(k) + ":" + canonicalize(obj[k])).join(",") + "}";
  }
  throw new Error(`JCS: cannot canonicalise a ${typeof value}`);
}
function without(obj, key) {
  const out = {};
  for (const k of Object.keys(obj)) if (k !== key) out[k] = obj[k];
  return out;
}
async function hashData(document, proofConfig2) {
  const cfg = without(proofConfig2, "proofValue");
  if (document["@context"] !== void 0) cfg["@context"] = document["@context"];
  const h1 = await sha256(canonicalize(cfg));
  const h2 = await sha256(canonicalize(without(document, "proof")));
  const out = new Uint8Array(64);
  out.set(h1, 0);
  out.set(h2, 32);
  return out;
}
function attachProof(document, proofConfig2, proofValue) {
  return { ...without(document, "proof"), proof: { ...without(proofConfig2, "proofValue"), proofValue } };
}
function encodeEd25519ProofValue(signature) {
  return "z" + base58Encode(signature);
}
function toBytes(key) {
  if (typeof key !== "string") return key;
  const s = key.trim();
  if (/^(0x)?[0-9a-fA-F]+$/.test(s) && (s.replace(/^0x/, "").length === 64 || s.replace(/^0x/, "").length === 128)) {
    return hexToBytes(s.replace(/^0x/, ""));
  }
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64 + "=".repeat((4 - b64.length % 4) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}
async function signHashDataEd25519(hashDataHex, privateKey) {
  const sig = await ed25519Sign(toBytes(privateKey), hexToBytes(hashDataHex));
  return encodeEd25519ProofValue(sig);
}
function proofConfig(cryptosuite, o) {
  const created = o.created ?? /* @__PURE__ */ new Date();
  const iso = created.getUTCMilliseconds() === 0 ? created.toISOString().replace(/\.\d{3}Z$/, "Z") : created.toISOString();
  const cfg = {
    type: DATA_INTEGRITY_PROOF,
    cryptosuite,
    created: iso,
    verificationMethod: o.verificationMethod,
    proofPurpose: o.proofPurpose
  };
  if (o.challenge) cfg.challenge = o.challenge;
  if (o.domain) cfg.domain = o.domain;
  return cfg;
}
async function signDocumentEd25519(document, privateKey, o) {
  const cfg = proofConfig(CRYPTOSUITE_EDDSA_JCS, o);
  const hd = await hashData(document, cfg);
  return attachProof(document, cfg, encodeEd25519ProofValue(await ed25519Sign(toBytes(privateKey), hd)));
}
async function verifyEd25519Proof(document, publicKey) {
  const proof = document.proof;
  if (!proof || proof.type !== DATA_INTEGRITY_PROOF || proof.cryptosuite !== CRYPTOSUITE_EDDSA_JCS) return false;
  const value = proof.proofValue;
  if (typeof value !== "string" || !value.startsWith("z")) return false;
  let pub;
  if (typeof publicKey === "string" && publicKey.startsWith("z")) {
    const raw = base58Decode(publicKey.slice(1));
    pub = raw.length === 34 && raw[0] === 237 && raw[1] === 1 ? raw.slice(2) : raw;
  } else {
    pub = toBytes(publicKey);
  }
  const hd = await hashData(document, proof);
  return ed25519Verify(pub, hd, base58Decode(value.slice(1)));
}

// src/credentials.ts
async function proofValueFor(prepared, signer) {
  if (signer.kind === "ed25519") {
    if (prepared.cryptosuite !== "eddsa-jcs-2022") {
      throw new Error(`the chosen key needs a wallet signature (${prepared.cryptosuite}); pass a wallet signer or pick an Ed25519 verificationMethod`);
    }
    return signHashDataEd25519(prepared.hashData, signer.privateKey);
  }
  if (!prepared.message) {
    throw new Error("the chosen key is an Ed25519 key; pass an ed25519 signer or pick the wallet verificationMethod");
  }
  return signer.signMessage(prepared.message);
}
var CredentialOperations = class {
  constructor(config) {
    this.config = config;
  }
  /**
   * Prepare a credential for the issuer to sign: the API validates the
   * claims, assigns its id, dates and revocation entry, and returns what to
   * sign. Complete it within 30 minutes with {@link completeCredential}.
   */
  async prepareCredential(params, opts) {
    return httpPost(this.config, "/api/v1/credentials/prepare", {
      issuer: params.issuerDid,
      issuerName: params.issuerName,
      subjectDid: params.subjectDid,
      type: params.type,
      credentialSubject: params.credentialSubject,
      schemaId: params.schemaId,
      expirationDate: params.expirationDate,
      ...opts?.verificationMethod ? { verificationMethod: opts.verificationMethod } : {}
    });
  }
  /** Submit the issuer's proofValue for a prepared credential. */
  async completeCredential(credentialId, proofValue) {
    return httpPost(this.config, "/api/v1/credentials/issue", { credentialId, proofValue });
  }
  /**
   * Issue a new Verifiable Credential, signed by its issuer.
   *
   * Pass `opts.signer` with the issuer's Ed25519 key or a wallet signer: the
   * credential is prepared, signed locally and completed. Without a signer
   * the API signs only if the issuer is the platform DID; any other issuer
   * gets a SIGNATURE_REQUIRED error.
   */
  async issueCredential(params, opts) {
    if (opts?.signer) {
      const prepared = await this.prepareCredential(params, { verificationMethod: opts.verificationMethod });
      return this.completeCredential(prepared.credentialId, await proofValueFor(prepared, opts.signer));
    }
    return httpPost(this.config, "/api/v1/credentials/issue", {
      issuer: params.issuerDid,
      issuerName: params.issuerName,
      subjectDid: params.subjectDid,
      type: params.type,
      credentialSubject: params.credentialSubject,
      schemaId: params.schemaId,
      expirationDate: params.expirationDate
    });
  }
  /**
   * Verify a Verifiable Credential.
   */
  async verifyCredential(vc) {
    return httpPost(
      this.config,
      "/api/v1/credentials/verify",
      { credential: vc }
    );
  }
  /**
   * Revoke a Verifiable Credential.
   */
  async revokeCredential(credentialId, reason) {
    await httpPost(
      this.config,
      "/api/v1/credentials/revoke",
      { credentialId, reason }
    );
  }
  /**
   * Get a credential by ID.
   */
  async getCredential(id) {
    const encoded = encodeDID(id);
    return httpGet(
      this.config,
      `/api/v1/credentials/${encoded}`
    );
  }
  /**
   * Check the revocation status of a credential.
   */
  async checkRevocationStatus(id) {
    const encoded = encodeDID(id);
    return httpGet(
      this.config,
      `/api/v1/credentials/${encoded}/status`
    );
  }
  /**
   * Create a Verifiable Presentation from one or more credentials.
   */
  async createPresentation(params) {
    return httpPost(
      this.config,
      "/api/v1/presentations/create",
      {
        holderDid: params.holderDid,
        credentialIds: params.credentialIds,
        challenge: params.challenge,
        domain: params.domain
      }
    );
  }
  /**
   * Prepare and sign a presentation with the holder's key: the result is a
   * signed presentation ready for a verifier.
   */
  async createSignedPresentation(params, signer) {
    const prepared = await this.createPresentation(params);
    return signPreparedPresentation(prepared, signer);
  }
  /**
   * Verify a Verifiable Presentation.
   */
  async verifyPresentation(vp, challenge, domain) {
    return httpPost(
      this.config,
      "/api/v1/presentations/verify",
      { presentation: vp, challenge, domain }
    );
  }
};
async function signPreparedPresentation(prepared, signer) {
  const proofValue = await proofValueFor(prepared, signer);
  return attachProof(prepared.presentation, prepared.proofConfig, proofValue);
}
async function signPreparedCredential(prepared, signer) {
  return proofValueFor(prepared, signer);
}

// src/agents.ts
function requireChain2(config, op, network) {
  if (network !== void 0) return requireEnabledChain(network);
  if (!config.chain) {
    throw new Error(`${op} with a signer needs a chain: pass { network: 'adi' | <chainId> } or set \`chain\` config (with agentTrustRegistry) on the IDAClient.`);
  }
  return config.chain;
}
function signalToInt(type) {
  switch (type) {
    case "task_completed":
    case "verification_success":
    case "attestation":
    case "delegation_received":
    case "uptime_ping":
      return 1;
    case "task_failed":
    case "verification_failure":
    case "complaint":
    case "delegation_revoked":
      return -1;
    default:
      return 0;
  }
}
var AgentOperations = class {
  constructor(config) {
    this.config = config;
  }
  /**
   * Register a new AI agent DID.
   *
   * Dual-mode: pass `opts.signer` AND `opts.agentDid` to self-sovereignly submit
   * AgentTrustRegistry.registerAgent() on-chain (the signer's wallet becomes the
   * agent's operator). `params.operator` is used as the on-chain operatorDid and
   * the agent card is built from name/modelInfo/capabilities. Returns the tx
   * result. Omit `opts.signer` for the legacy API path (server mints the DID).
   */
  async registerAgent(params, opts) {
    if (opts?.signer) {
      if (!opts.agentDid) {
        throw new Error("registerAgent with a signer requires opts.agentDid (the agent DID to register on-chain).");
      }
      const agentCard = {
        name: params.name,
        description: params.description,
        modelInfo: params.modelInfo,
        capabilities: params.capabilities,
        autonomyLevel: mapAutonomyLevelToInt(params.autonomyLevel || "Intern"),
        services: params.services
      };
      return registerAgentOnChain(opts.signer, requireChain2(this.config, "registerAgent", opts?.network), opts.agentDid, params.operator, agentCard);
    }
    const body = {
      operator: params.operator,
      modelInfo: params.modelInfo,
      capabilities: params.capabilities.map((name) => ({
        name,
        description: name
      })),
      autonomyLevel: mapAutonomyLevelToInt(params.autonomyLevel || "Intern"),
      services: params.services,
      ...params.publicKeyMultibase ? { publicKeyMultibase: params.publicKeyMultibase } : {}
    };
    return httpPost(
      this.config,
      "/api/v1/agents",
      body
    );
  }
  /**
   * Get agent details by DID.
   */
  async getAgent(agentDid) {
    const encoded = encodeDID(agentDid);
    const raw = await httpGet(
      this.config,
      `/api/v1/agents/${encoded}`
    );
    return raw.agent ?? raw;
  }
  /**
   * Update an agent's metadata.
   *
   * Dual-mode: pass `opts.signer` to submit AgentTrustRegistry.updateAgentCard()
   * on-chain (signer must be the agent operator); omit for the legacy API path.
   */
  async updateAgent(agentDid, updates, opts) {
    if (opts?.signer) {
      return updateAgentCardOnChain(opts.signer, requireChain2(this.config, "updateAgent", opts?.network), agentDid, updates);
    }
    const encoded = encodeDID(agentDid);
    return httpPut(
      this.config,
      `/api/v1/agents/${encoded}`,
      updates
    );
  }
  /**
   * Decommission an agent (permanent deactivation with cascading revocation).
   *
   * Dual-mode: pass `opts.signer` to submit AgentTrustRegistry.decommissionAgent()
   * on-chain (signer must be the agent operator); omit for the legacy API path.
   */
  async decommissionAgent(agentDid, opts) {
    if (opts?.signer) {
      return decommissionAgentOnChain(opts.signer, requireChain2(this.config, "decommissionAgent", opts?.network), agentDid);
    }
    const encoded = encodeDID(agentDid);
    await httpDelete(
      this.config,
      `/api/v1/agents/${encoded}`
    );
  }
  /**
   * Suspend an agent. Unlike decommissioning this can be undone with
   * {@link reactivateAgent}. While suspended the agent cannot mint or redeem
   * IBCTs or issue or receive delegations. `txHash` records an on-chain
   * status change the operator already submitted.
   */
  async suspendAgent(agentDid, reason, txHash) {
    return httpPost(
      this.config,
      `/api/v1/agents/${encodeDID(agentDid)}/suspend`,
      { reason, ...txHash ? { txHash } : {} }
    );
  }
  /** Lift a suspension. Fails with 409 when the agent is not suspended. */
  async reactivateAgent(agentDid, txHash) {
    return httpPost(
      this.config,
      `/api/v1/agents/${encodeDID(agentDid)}/reactivate`,
      txHash ? { txHash } : {}
    );
  }
  /**
   * Set an agent's autonomy level on-chain (self-sovereign only; the signer must
   * be the agent operator). There is no legacy API equivalent for this write.
   */
  async setAutonomyLevel(agentDid, level, opts) {
    return setAutonomyLevelOnChain(opts.signer, requireChain2(this.config, "setAutonomyLevel", opts?.network), agentDid, level);
  }
  /**
   * Get an agent's A2A Agent Card, signed by the platform.
   */
  async getAgentCard(agentDid) {
    return httpGet(
      this.config,
      `/.well-known/agent.json?did=${encodeDID(agentDid)}`
    );
  }
  /**
   * Verify a peer's A2A Agent Card: its signature, and whether the agent is
   * active now (a card is only a snapshot).
   */
  async verifyAgentCard(card) {
    return httpPost(this.config, "/api/v1/a2a/verify-card", card);
  }
  /**
   * MCP-I, for a tool provider: verify the calling agent's capability token
   * (action "tool:<name>", audience your agent DID) before running the tool.
   * The token is accepted once; the result carries a signed receipt.
   */
  async verifyToolCall(toolId, token) {
    return httpPost(this.config, "/api/v1/mcp/verify-call", { toolId, token });
  }
  /** Prepare a delegation for the delegator to sign. */
  async prepareDelegation(params) {
    return httpPost(this.config, "/api/v1/delegations/prepare", params);
  }
  /** Submit the delegator's proofValue for a prepared delegation. */
  async completeDelegation(credentialId, proofValue) {
    return httpPost(this.config, "/api/v1/delegations", { credentialId, proofValue });
  }
  /**
   * Issue a signed delegation: prepare, sign with the delegator's key (or a
   * wallet signer) and submit.
   */
  async issueDelegation(params, signer) {
    const prepared = await this.prepareDelegation(params);
    return this.completeDelegation(prepared.credentialId, await signPreparedCredential(prepared, signer));
  }
  /**
   * Issue a delegation from a human/org DID to an agent.
   * @deprecated Use {@link issueDelegation}; delegations must be signed by
   * the delegator. This maps the old parameters onto it.
   */
  async issueAgentDelegation(params, signer) {
    return this.issueDelegation({
      delegatorDid: params.delegatorDid,
      delegateDid: params.agentDid,
      scope: params.scope.actions,
      constraints: params.scope.constraints,
      validUntil: params.scope.expiry
    }, signer);
  }
  /** Verify a delegation's whole chain, optionally for an action. */
  async verifyDelegation(delegationId, action) {
    return httpPost(this.config, `/api/v1/delegations/${encodeURIComponent(delegationId)}/verify`, action ? { action } : {});
  }
  /** Revoke a delegation and everything attenuated from it. */
  async revokeDelegation(delegationId, reason) {
    return httpPost(this.config, `/api/v1/delegations/${encodeURIComponent(delegationId)}/revoke`, reason ? { reason } : {});
  }
  /**
   * Verify a complete delegation chain from root to leaf.
   *
   * @deprecated Use `IDAClient.verifyDelegationChainSteps()` instead. The
   * Phase-7-A server returns the new `VerifyChainResult` shape (4 fields);
   * this legacy method's `ChainVerificationResult` is preserved for
   * backwards compatibility but no longer matches the wire response.
   */
  async verifyDelegationChain(chain) {
    return httpPost(
      this.config,
      "/api/v1/delegations/verify-chain",
      {
        agentDid: chain.leafAgent,
        delegationChain: chain.hops.map((hop) => ({
          credentialId: hop.delegationId
        }))
      }
    );
  }
  /**
   * List the agents you operate, optionally narrowed by label.
   *
   * ```ts
   * await client.listAgents();                                   // all of yours
   * await client.listAgents({ labels: { team: 'finance' } });     // one label
   * await client.listAgents({ labels: { team: 'finance', env: 'prod' } });
   * ```
   *
   * Several labels must all match. Results are scoped to the caller: an agent
   * appears when its operator is your own subject or a DID you own, so the API
   * key must belong to the user who owns the operator DID — a key issued from
   * the portal's Settings page, not a shared service key whose subject owns
   * nothing.
   */
  async listAgents(query = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query.labels ?? {})) {
      params.append("label", `${key}:${value}`);
    }
    if (query.limit !== void 0) params.set("limit", query.limit.toString());
    if (query.offset !== void 0) params.set("offset", query.offset.toString());
    const qs = params.toString();
    return httpGet(
      this.config,
      `/api/v1/agents/me${qs ? `?${qs}` : ""}`
    );
  }
  /**
   * Discover agents matching the given query criteria.
   */
  async discoverAgents(query) {
    const params = new URLSearchParams();
    if (query.capability) params.set("capability", query.capability);
    if (query.minTrustScore !== void 0) params.set("minTrustScore", query.minTrustScore.toString());
    if (query.state) params.set("state", query.state);
    if (query.operator) params.set("operator", query.operator);
    if (query.limit !== void 0) params.set("limit", query.limit.toString());
    if (query.offset !== void 0) params.set("offset", query.offset.toString());
    return httpGet(
      this.config,
      `/api/v1/agents/discover?${params.toString()}`
    );
  }
  /**
   * Provision an agent through the AgentSight pipeline.
   * Mints a DID, issues an AgentIdentityCredential, and returns a one-time IBCT signing key.
   * Use this instead of registerAgent when integrating with the AgentSight platform.
   */
  async provisionAgent(params) {
    return httpPost(
      this.config,
      "/api/v1/agents/provision",
      params
    );
  }
  /**
   * Record a trust signal for an agent (task_completed, policy_violation, etc).
   * Updates the agent's trust score and autonomy level on-chain.
   */
  async recordTrustSignal(agentDid, signal, opts) {
    if (opts?.signer) {
      return submitTrustSignalOnChain(
        opts.signer,
        requireChain2(this.config, "recordTrustSignal", opts?.network),
        agentDid,
        signalToInt(signal.type),
        signal.details ?? signal.source ?? signal.type
      );
    }
    const encoded = encodeDID(agentDid);
    return httpPost(
      this.config,
      `/api/v1/agents/${encoded}/signals`,
      signal
    );
  }
  /**
   * Register a webhook subscription for agent lifecycle events.
   * Events: agent.provisioned, agent.revoked, agent.trust_updated
   */
  async registerWebhook(params) {
    return httpPost(
      this.config,
      "/api/v1/webhooks",
      params
    );
  }
  /**
   * List all webhook subscriptions for an owner DID.
   */
  async listWebhooks(ownerDid) {
    const encoded = encodeDID(ownerDid);
    return httpGet(
      this.config,
      `/api/v1/webhooks?ownerDid=${encoded}`
    );
  }
  /**
   * Delete a webhook subscription by ID.
   */
  async deleteWebhook(subscriptionId) {
    await httpDelete(
      this.config,
      `/api/v1/webhooks/${subscriptionId}`
    );
  }
  /**
   * Get webhook delivery history for a subscription.
   */
  async getWebhookDeliveries(subscriptionId, limit = 20) {
    return httpGet(
      this.config,
      `/api/v1/webhooks/${subscriptionId}/deliveries?limit=${limit}`
    );
  }
  /**
   * Get the audit log for an agent.
   */
  async getAuditLog(agentDid, limit = 50, offset = 0) {
    const encoded = encodeDID(agentDid);
    return httpGet(
      this.config,
      `/api/v1/agents/${encoded}/audit-log?limit=${limit}&offset=${offset}`
    );
  }
};
function mapAutonomyLevelToInt(level) {
  switch (level.toLowerCase()) {
    case "intern":
      return 0;
    case "junior":
      return 1;
    case "senior":
      return 2;
    case "principal":
      return 3;
    default:
      return 0;
  }
}

// src/zkp.ts
var ZKPOperations = class {
  constructor(config) {
    this.config = config;
  }
  /**
   * Generate a fresh cryptographic challenge for ZK proof generation.
   */
  async generateChallenge() {
    return httpPost(this.config, "/api/v1/zkp/challenge", {});
  }
  /**
   * Generate a ZK proof for credential predicates without revealing actual values.
   *
   * @example
   * ```typescript
   * const proof = await client.generateZKProof({
   *   credentialId: 'urn:uuid:abc123',
   *   proverDid: 'did:adi:xyz',
   *   privateKey: '<base64url-encoded-ed25519-key>',
   *   credentialSubject: { age: 25, country: 'US' },
   *   predicates: [
   *     { attributeName: 'age', type: 'gte', value: 18 },
   *     { attributeName: 'country', type: 'membership', memberSet: ['US', 'UK', 'EU'] },
   *   ],
   * });
   * ```
   */
  async generateZKProof(params) {
    return httpPost(this.config, "/api/v1/zkp/proofs", params);
  }
  /**
   * Verify a ZK proof off-chain.
   *
   * @param proof - The ZK proof to verify.
   * @param proverDid - The DID of the prover (used to resolve the public key).
   */
  async verifyZKProof(proof, proverDid) {
    return httpPost(this.config, "/api/v1/zkp/verify", {
      proof,
      proverDid
    });
  }
  /**
   * Retrieve a stored ZK proof by ID.
   */
  async getZKProof(proofId) {
    const encoded = encodeURIComponent(proofId);
    return httpGet(this.config, `/api/v1/zkp/proofs/${encoded}`);
  }
  // --- Phase-7-A: anchoring & delegated proofs ---
  /**
   * Anchor a previously-generated proof on the `ZKProofVerifier` registry.
   *
   * The server returns immediately with `status: 'pending'` and a `txHash`;
   * the indexer flips the status to `confirmed` (or `failed`) asynchronously.
   * Pass `Idempotency-Key` to make retries safe; the SDK auto-generates one
   * when not supplied.
   */
  async anchorZKProof(proofId, opts) {
    const encoded = encodeURIComponent(proofId);
    const idempotencyKey = opts?.idempotencyKey ?? generateIdempotencyKey();
    const body = {};
    if (opts?.syncWaitMs !== void 0) body.syncWaitMs = opts.syncWaitMs;
    if (opts?.predicateHash) body.predicateHash = opts.predicateHash;
    return httpPostWithHeaders(
      this.config,
      `/api/v1/zkp/proofs/${encoded}/anchor`,
      body,
      { "Idempotency-Key": idempotencyKey }
    );
  }
  /**
   * Read the current anchor record for a proof.
   *
   * Throws `IDAHTTPError` with status 404 when the proof has no anchor.
   */
  async getZKAnchor(proofId) {
    const encoded = encodeURIComponent(proofId);
    return httpGet(this.config, `/api/v1/zkp/proofs/${encoded}/anchor`);
  }
  /**
   * List anchor records, paginated.
   *
   * Common filters: `did` (submitter), `since` (ISO-8601), `limit` (≤ 200).
   * The optional `kind` filter is honoured client-side if the API does not
   * yet recognise it.
   */
  async listZKAnchors(params) {
    const search = new URLSearchParams();
    if (params?.did) search.set("did", params.did);
    if (params?.holderDid) search.set("holderDid", params.holderDid);
    if (params?.credentialId) search.set("credentialId", params.credentialId);
    if (params?.since) search.set("since", params.since);
    if (params?.until) search.set("until", params.until);
    if (params?.status) search.set("status", params.status);
    if (params?.kind) search.set("kind", params.kind);
    if (params?.limit !== void 0) search.set("limit", params.limit.toString());
    if (params?.offset !== void 0) search.set("offset", params.offset.toString());
    const qs = search.toString();
    const path = qs ? `/api/v1/zkp/anchors?${qs}` : "/api/v1/zkp/anchors";
    const raw = await httpGet(this.config, path);
    let items = Array.isArray(raw.items) ? raw.items : [];
    if (params?.kind === "delegated") {
      items = items.filter((a) => a.kind === "delegated" || a.holderDid != null && a.holderDid !== a.submitterDid);
    } else if (params?.kind === "self") {
      items = items.filter((a) => a.kind === "self" || a.holderDid == null || a.holderDid === a.submitterDid);
    }
    return {
      data: items,
      pagination: {
        totalCount: raw.totalCount ?? items.length,
        limit: raw.limit ?? params?.limit ?? items.length,
        offset: raw.offset ?? params?.offset ?? 0
      }
    };
  }
  /**
   * Generate a delegated ZK proof — an agent proves a predicate on the
   * holder's behalf, witnessed by an IBCT.
   *
   * The agent must have autonomy ≥ Junior on `AgentTrustRegistry` and the
   * IBCT scope must cover the predicate.
   */
  async generateDelegatedZKProof(params) {
    return httpPost(this.config, "/api/v1/zkp/delegated-proofs", {
      holderDid: params.holderDid,
      agentDid: params.agentDid,
      credentialId: params.credentialId,
      predicate: params.predicate,
      ibct: params.ibct,
      anchor: params.anchor ?? false,
      syncWaitMs: params.syncWaitMs,
      threshold: params.threshold
    });
  }
  /**
   * Verify a multi-hop delegation chain (closes the ENG-1 server gap).
   *
   * `chain` is ordered root → leaf. Returns `valid: false` with per-hop
   * diagnostics on chain integrity failures (revoked hop, scope widening,
   * etc.); the call itself returns 200 OK.
   */
  async verifyDelegationChain(chain, opts) {
    const leaf = chain[chain.length - 1];
    return httpPost(this.config, "/api/v1/delegations/verify-chain", {
      agentDid: opts?.agentDid ?? leaf?.subject ?? "",
      delegationChain: chain.map((step) => ({ credentialId: step.credentialId })),
      action: opts?.action
    });
  }
  /**
   * Revoke a previously-anchored proof. Calls
   * `ZKProofVerifier.revokeProof(proofHash)` and updates the anchor row.
   */
  async revokeZKProof(proofId, opts) {
    const encoded = encodeURIComponent(proofId);
    return httpPost(
      this.config,
      `/api/v1/zkp/proofs/${encoded}/revoke`,
      {
        reason: opts?.reason ?? "",
        syncWaitMs: opts?.syncWaitMs
      }
    );
  }
};
function generateIdempotencyKey() {
  const g = globalThis;
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  if (g.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    g.crypto.getRandomValues(bytes);
    bytes[6] = bytes[6] & 15 | 64;
    bytes[8] = bytes[8] & 63 | 128;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : r & 3 | 8).toString(16);
  });
}
async function httpPostWithHeaders(config, path, body, extra) {
  const url = config.apiUrl.replace(/\/$/, "") + path;
  const headers = {
    "Content-Type": "application/json",
    "Accept": "application/json",
    ...extra
  };
  if (config.apiKey) headers["Authorization"] = `Bearer ${config.apiKey}`;
  const init = {
    method: "POST",
    headers,
    body: JSON.stringify(body ?? {}),
    signal: config.timeout ? AbortSignal.timeout(config.timeout) : void 0
  };
  const response = await fetch(url, init);
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorCode = "UNKNOWN_ERROR";
    try {
      const errBody = await response.json();
      if (errBody.error) {
        errorMessage = errBody.error.message ?? errorMessage;
        errorCode = errBody.error.code ?? errorCode;
      }
    } catch {
    }
    const err = new Error(errorMessage);
    err.name = "IDAHTTPError";
    err.status = response.status;
    err.code = errorCode;
    throw err;
  }
  if (response.status === 204) return void 0;
  const json = await response.json();
  if (json && typeof json === "object" && "data" in json) {
    return json.data;
  }
  return json;
}

// src/tools.ts
var ToolOperations = class {
  constructor(config) {
    this.config = config;
  }
  async list(agentDid, opts) {
    const search = new URLSearchParams();
    if (opts?.includeRevoked) search.set("includeRevoked", "true");
    if (opts?.limit !== void 0) search.set("limit", opts.limit.toString());
    if (opts?.cursor) search.set("cursor", opts.cursor);
    const qs = search.toString();
    const path = qs ? `/api/v1/agents/${encodeDID(agentDid)}/tools?${qs}` : `/api/v1/agents/${encodeDID(agentDid)}/tools`;
    const raw = await httpGet(this.config, path);
    const items = Array.isArray(raw.items) ? raw.items : [];
    return {
      data: items,
      pagination: {
        totalCount: raw.totalCount ?? items.length,
        limit: raw.limit ?? opts?.limit ?? items.length,
        offset: raw.offset ?? 0,
        nextCursor: raw.nextCursor ?? null,
        hasMore: raw.hasMore ?? false
      }
    };
  }
  async get(agentDid, toolId) {
    return httpGet(
      this.config,
      `/api/v1/agents/${encodeDID(agentDid)}/tools/${encodeURIComponent(toolId)}`
    );
  }
  async register(agentDid, params, opts) {
    const headers = {};
    if (opts?.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
    return requestWithHeaders(
      this.config,
      "POST",
      `/api/v1/agents/${encodeDID(agentDid)}/tools`,
      params,
      headers
    );
  }
  /**
   * Update a tool. `rowVersion` is the optimistic-concurrency token from the
   * latest GET; the server returns `409 STALE_VERSION` if a concurrent edit
   * landed first, in which case the caller should refetch and retry.
   */
  async update(agentDid, toolId, params, rowVersion) {
    return requestWithHeaders(
      this.config,
      "PUT",
      `/api/v1/agents/${encodeDID(agentDid)}/tools/${encodeURIComponent(toolId)}`,
      { ...params, ifVersion: rowVersion },
      {}
    );
  }
  async revoke(agentDid, toolId, reason) {
    return requestWithHeaders(
      this.config,
      "POST",
      `/api/v1/agents/${encodeDID(agentDid)}/tools/${encodeURIComponent(toolId)}/revoke`,
      { reason },
      {}
    );
  }
};
async function requestWithHeaders(config, method, path, body, extra) {
  const url = config.apiUrl.replace(/\/$/, "") + path;
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...extra
  };
  if (config.apiKey) headers["Authorization"] = `Bearer ${config.apiKey}`;
  const init = {
    method,
    headers,
    body: JSON.stringify(body ?? {}),
    signal: config.timeout ? AbortSignal.timeout(config.timeout) : void 0
  };
  const response = await fetch(url, init);
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorCode = "UNKNOWN_ERROR";
    try {
      const errBody = await response.json();
      if (errBody.error) {
        errorMessage = errBody.error.message ?? errorMessage;
        errorCode = errBody.error.code ?? errorCode;
      }
    } catch {
    }
    const err = new Error(errorMessage);
    err.name = "IDAHTTPError";
    err.status = response.status;
    err.code = errorCode;
    throw err;
  }
  if (response.status === 204) return void 0;
  const json = await response.json();
  if (json && typeof json === "object" && "data" in json) {
    return json.data;
  }
  return json;
}

// src/policies.ts
var PolicyOperations = class {
  constructor(config) {
    this.config = config;
  }
  async list(opts) {
    const search = new URLSearchParams();
    if (opts?.includeRevoked) search.set("includeRevoked", "true");
    if (opts?.limit !== void 0) search.set("limit", opts.limit.toString());
    if (opts?.cursor) search.set("cursor", opts.cursor);
    const qs = search.toString();
    const path = qs ? `/api/v1/operators/me/policies?${qs}` : `/api/v1/operators/me/policies`;
    const raw = await httpGet(this.config, path);
    let items = Array.isArray(raw.items) ? raw.items : [];
    if (opts?.ownerDid) items = items.filter((p) => p.ownerDid === opts.ownerDid);
    return {
      data: items,
      nextCursor: raw.nextCursor ?? null,
      hasMore: raw.hasMore ?? false
    };
  }
  async get(policyId) {
    return httpGet(
      this.config,
      `/api/v1/operators/me/policies/${encodeURIComponent(policyId)}`
    );
  }
  /**
   * Create (when `id` is absent) or update (when `id` is present) a policy.
   *
   * Updates require `ifVersion` for optimistic concurrency.
   */
  async upsert(params) {
    const isUpdate = !!params.id;
    const path = isUpdate ? `/api/v1/operators/me/policies/${encodeURIComponent(params.id)}` : `/api/v1/operators/me/policies`;
    const method = isUpdate ? "PUT" : "POST";
    const { id: _id, ...body } = params;
    void _id;
    return requestWithHeaders2(this.config, method, path, body, {});
  }
  async delete(policyId) {
    await httpDelete(
      this.config,
      `/api/v1/operators/me/policies/${encodeURIComponent(policyId)}`
    );
  }
  /**
   * Mint a public IBCT scoped to the agent. Calls
   * `POST /api/v1/agents/{did}/ibcts`. The brief lists this as NEW (the
   * legacy implementation kept IBCTs internal to the delegated-proof flow);
   * see `01_architecture.md` §10.7.
   */
  async mintIBCT(agentDid, params, opts) {
    const headers = {};
    if (opts?.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
    const resp = await requestWithHeaders2(
      this.config,
      "POST",
      `/api/v1/agents/${encodeDID(agentDid)}/ibcts`,
      params,
      headers
    );
    if (resp && typeof resp === "object" && "ibct" in resp) {
      return resp.ibct;
    }
    return resp;
  }
  /**
   * Redeem a token before performing the action it grants. The server checks
   * the signature, validity window, action, redeemer and agent status, and
   * accepts each token once; a second redemption fails with 409.
   */
  async redeemIBCT(ibct, action) {
    return httpPost(this.config, "/api/v1/ibcts/redeem", {
      ibct,
      ...action ? { action } : {}
    });
  }
  /** Fetch the public key that verifies tokens this server mints. */
  async getIBCTSigningKey() {
    return httpGet(this.config, "/api/v1/ibcts/signing-key");
  }
};
async function requestWithHeaders2(config, method, path, body, extra) {
  const url = config.apiUrl.replace(/\/$/, "") + path;
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...extra
  };
  if (config.apiKey) headers["Authorization"] = `Bearer ${config.apiKey}`;
  const init = {
    method,
    headers,
    body: JSON.stringify(body ?? {}),
    signal: config.timeout ? AbortSignal.timeout(config.timeout) : void 0
  };
  const response = await fetch(url, init);
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorCode = "UNKNOWN_ERROR";
    try {
      const errBody = await response.json();
      if (errBody.error) {
        errorMessage = errBody.error.message ?? errorMessage;
        errorCode = errBody.error.code ?? errorCode;
      }
    } catch {
    }
    const err = new Error(errorMessage);
    err.name = "IDAHTTPError";
    err.status = response.status;
    err.code = errorCode;
    throw err;
  }
  if (response.status === 204) return void 0;
  const json = await response.json();
  if (json && typeof json === "object" && "data" in json) {
    return json.data;
  }
  return json;
}

// src/audit.ts
var eventSourceFactory = null;
function setEventSourceFactory(ctor) {
  eventSourceFactory = ctor;
}
function resolveEventSource() {
  if (eventSourceFactory) return eventSourceFactory;
  const g = globalThis;
  if (!g.EventSource) {
    throw new Error("EventSource is not available in this environment");
  }
  return g.EventSource;
}
var AuditOperations = class {
  constructor(config) {
    this.config = config;
  }
  /**
   * Cursor-paginated audit-log history.
   *
   * Wire endpoint: `GET /agents/{did}/audit-log`.
   */
  async getAuditLog(agentDid, params) {
    const search = new URLSearchParams();
    if (params?.eventType) {
      const types = Array.isArray(params.eventType) ? params.eventType : [params.eventType];
      for (const t of types) search.append("eventType", t);
    }
    if (params?.since) search.set("since", params.since);
    if (params?.until) search.set("until", params.until);
    if (params?.actorDid) search.set("actorDid", params.actorDid);
    if (params?.cursor) search.set("cursor", params.cursor);
    if (params?.limit !== void 0) search.set("limit", params.limit.toString());
    const qs = search.toString();
    const path = qs ? `/api/v1/agents/${encodeDID(agentDid)}/audit-log?${qs}` : `/api/v1/agents/${encodeDID(agentDid)}/audit-log`;
    const raw = await httpGet(this.config, path);
    return {
      data: Array.isArray(raw.items) ? raw.items : [],
      nextCursor: raw.nextCursor ?? null,
      hasMore: raw.hasMore ?? false
    };
  }
  /**
   * Subscribe to the per-agent audit stream over SSE.
   *
   * Workflow (architect-locked):
   * 1. POST `/audit-stream/ticket` to obtain a 30s JWT.
   * 2. Open `EventSource` against `/audit-stream?ticket=…` (EventSource
   *    cannot send custom headers, hence the ticket pattern).
   * 3. Track the last seen event id and resume on reconnect.
   * 4. Reconnect with exponential backoff (max 30s) up to
   *    `maxReconnectAttempts` (default 5). On giveup, invoke `onError`.
   *
   * Each SSE frame may carry a single event or a JSON array of events
   * (see `02_api_spec.md` §2.3); both shapes are flattened here so the
   * caller's `onEvent` receives one event at a time.
   */
  subscribeAuditStream(agentDid, opts) {
    const maxAttempts = opts.maxReconnectAttempts ?? 5;
    let lastEventId = opts.lastEventId;
    let es = null;
    let closed = false;
    let attempts = 0;
    let reconnectTimer = null;
    const cleanup = () => {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (es) {
        try {
          es.close();
        } catch {
        }
        es = null;
      }
    };
    const handleFrame = (data, frameId) => {
      let parsed = null;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }
      const events = Array.isArray(parsed) ? parsed : [parsed];
      for (const ev of events) {
        if (!ev) continue;
        if (ev.id) lastEventId = ev.id;
        try {
          opts.onEvent(ev);
        } catch {
        }
      }
      if (frameId) lastEventId = frameId;
    };
    const connect = async () => {
      if (closed) return;
      try {
        const ticket = await mintTicket(this.config, agentDid);
        const params = new URLSearchParams();
        params.set("ticket", ticket);
        if (lastEventId) params.set("lastEventId", lastEventId);
        const url = this.config.apiUrl.replace(/\/$/, "") + `/api/v1/agents/${encodeDID(agentDid)}/audit-stream?${params.toString()}`;
        const Ctor = resolveEventSource();
        es = new Ctor(url);
        es.onopen = () => {
          attempts = 0;
        };
        const onMessage = (ev) => {
          handleFrame(ev.data, ev.lastEventId);
        };
        if (typeof es.addEventListener === "function") {
          es.addEventListener("audit", onMessage);
        } else {
          es.onmessage = onMessage;
        }
        es.onerror = () => {
          if (closed) return;
          cleanup();
          attempts += 1;
          if (attempts > maxAttempts) {
            opts.onError?.(new Error(`Audit stream failed after ${attempts} attempts`));
            closed = true;
            return;
          }
          const delayMs = Math.min(3e4, 500 * 2 ** (attempts - 1));
          reconnectTimer = setTimeout(connect, delayMs);
        };
      } catch (err) {
        if (closed) return;
        attempts += 1;
        if (attempts > maxAttempts) {
          opts.onError?.(err instanceof Error ? err : new Error(String(err)));
          closed = true;
          return;
        }
        const delayMs = Math.min(3e4, 500 * 2 ** (attempts - 1));
        reconnectTimer = setTimeout(connect, delayMs);
      }
    };
    void connect();
    return {
      close: () => {
        closed = true;
        cleanup();
      }
    };
  }
};
async function mintTicket(config, agentDid) {
  const url = config.apiUrl.replace(/\/$/, "") + `/api/v1/agents/${encodeDID(agentDid)}/audit-stream/ticket`;
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json"
  };
  if (config.apiKey) headers["Authorization"] = `Bearer ${config.apiKey}`;
  const response = await fetch(url, {
    method: "POST",
    headers,
    body: "{}",
    signal: config.timeout ? AbortSignal.timeout(config.timeout) : void 0
  });
  if (!response.ok) {
    const err = new Error(`Ticket mint failed: HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  const raw = await response.json();
  const data = raw && typeof raw === "object" && "data" in raw ? raw.data : raw;
  if (!data?.ticket) throw new Error("Ticket mint response missing `ticket`");
  return data.ticket;
}

// src/presentations.ts
var PresentationOperations = class {
  constructor(config) {
    this.config = config;
  }
  /** Create a Presentation Definition. */
  async createPresentationDefinition(params, opts) {
    const headers = {};
    if (opts?.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
    return requestWithHeaders3(
      this.config,
      "POST",
      "/api/v1/verifier/presentation-definitions",
      params,
      headers
    );
  }
  /** Cursor-paginated list of PDs the caller owns. */
  async listPresentationDefinitions(opts) {
    const search = new URLSearchParams();
    if (opts?.cursor) search.set("cursor", opts.cursor);
    if (opts?.limit !== void 0) search.set("limit", String(opts.limit));
    if (opts?.includeRevoked) search.set("includeRevoked", "true");
    if (opts?.verifierDid) search.set("verifierDid", opts.verifierDid);
    const qs = search.toString();
    const path = qs ? `/api/v1/verifier/presentation-definitions?${qs}` : "/api/v1/verifier/presentation-definitions";
    const raw = await httpGet(this.config, path);
    return {
      data: Array.isArray(raw.items) ? raw.items : [],
      nextCursor: raw.nextCursor ?? void 0
    };
  }
  /** Owner-scoped fetch of a single PD with full body. */
  async getPresentationDefinition(id) {
    return httpGet(
      this.config,
      `/api/v1/verifier/presentation-definitions/${encodeURIComponent(id)}`
    );
  }
  /**
   * Public, no-auth holder fetch.
   *
   * Strips operator metadata; mints a fresh `challenge` and `domain` for the
   * subsequent `evaluatePresentation` call. Anyone in possession of the URL
   * can call this — that is by design (architecture §7.1).
   */
  async getPublicPresentationDefinition(id) {
    return httpGet(
      this.config,
      `/api/v1/presentations/definitions/${encodeURIComponent(id)}`
    );
  }
  /**
   * Update a PD with optimistic CAS.
   *
   * @param id        — PD id.
   * @param params    — full replacement body (PUT semantics).
   * @param ifVersion — current version from the latest GET; mismatch → `409 PD_VERSION_CONFLICT`.
   */
  async updatePresentationDefinition(id, params, ifVersion) {
    return requestWithHeaders3(
      this.config,
      "PUT",
      `/api/v1/verifier/presentation-definitions/${encodeURIComponent(id)}`,
      { ...params, ifVersion },
      {}
    );
  }
  /** Soft-delete (revoke) a PD. The row stays for audit; `revoked: true` is set. */
  async deletePresentationDefinition(id, reason) {
    await requestWithHeaders3(
      this.config,
      "DELETE",
      `/api/v1/verifier/presentation-definitions/${encodeURIComponent(id)}`,
      reason ? { reason } : {},
      {}
    );
  }
  /**
   * Evaluate a Verifiable Presentation against a Presentation Definition.
   *
   * Returns per-input-descriptor outcomes; never short-circuits — the full
   * `descriptorOutcomes` array is always populated for explainability
   * (architecture §6).
   */
  async evaluatePresentation(params) {
    return requestWithHeaders3(
      this.config,
      "POST",
      "/api/v1/presentations/evaluate",
      params,
      {}
    );
  }
};
async function requestWithHeaders3(config, method, path, body, extra) {
  const url = config.apiUrl.replace(/\/$/, "") + path;
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...extra
  };
  if (config.apiKey) headers["Authorization"] = `Bearer ${config.apiKey}`;
  const init = {
    method,
    headers,
    body: JSON.stringify(body ?? {}),
    signal: config.timeout ? AbortSignal.timeout(config.timeout) : void 0
  };
  const response = await fetch(url, init);
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorCode = "UNKNOWN_ERROR";
    try {
      const errBody = await response.json();
      if (errBody.error) {
        errorMessage = errBody.error.message ?? errorMessage;
        errorCode = errBody.error.code ?? errorCode;
      }
    } catch {
    }
    const err = new Error(errorMessage);
    err.name = "IDAHTTPError";
    err.status = response.status;
    err.code = errorCode;
    throw err;
  }
  if (response.status === 204) return void 0;
  const text = await response.text();
  if (!text) return void 0;
  const json = JSON.parse(text);
  if (json && typeof json === "object" && "data" in json) {
    return json.data;
  }
  return json;
}

// src/namespaces.ts
function buildNamespaces(client) {
  return {
    presentations: {
      createDefinition: client.createPresentationDefinition.bind(client),
      listDefinitions: client.listPresentationDefinitions.bind(client),
      getDefinition: client.getPresentationDefinition.bind(client),
      getPublicDefinition: client.getPublicPresentationDefinition.bind(client),
      updateDefinition: client.updatePresentationDefinition.bind(client),
      deleteDefinition: client.deletePresentationDefinition.bind(client),
      evaluate: client.evaluatePresentation.bind(client),
      verify: client.verifyPresentation.bind(client),
      create: client.createPresentation.bind(client)
    },
    tools: {
      list: client.listTools.bind(client),
      get: client.getTool.bind(client),
      register: client.registerTool.bind(client),
      update: client.updateTool.bind(client),
      revoke: client.revokeTool.bind(client)
    },
    policies: {
      list: client.listPolicies.bind(client),
      get: client.getPolicy.bind(client),
      upsert: client.upsertPolicy.bind(client),
      delete: client.deletePolicy.bind(client),
      mintIBCT: client.mintIBCT.bind(client)
    },
    audit: {
      getLog: client.getAuditLog.bind(client),
      subscribeStream: client.subscribeAuditStream.bind(client)
    },
    zkp: {
      generateChallenge: client.generateZKChallenge.bind(client),
      generateProof: client.generateZKProof.bind(client),
      verifyProof: client.verifyZKProof.bind(client),
      getProof: client.getZKProof.bind(client),
      anchorProof: client.anchorZKProof.bind(client),
      getAnchor: client.getZKAnchor.bind(client),
      listAnchors: client.listZKAnchors.bind(client),
      generateDelegatedProof: client.generateDelegatedZKProof.bind(client),
      verifyDelegationChain: client.verifyDelegationChainSteps.bind(client),
      revokeProof: client.revokeZKProof.bind(client)
    }
  };
}
function buildMarketplaceNamespace(client) {
  return {
    getConfig: client.getMarketplaceConfig.bind(client),
    listListings: client.listListings.bind(client),
    getListing: client.getListing.bind(client),
    createListing: client.createListing.bind(client),
    updateListing: client.updateListing.bind(client),
    deleteListing: client.deleteListing.bind(client),
    pauseListing: client.pauseListing.bind(client),
    resumeListing: client.resumeListing.bind(client),
    listMyListings: client.listMyListings.bind(client),
    getMyEarnings: client.getMyEarnings.bind(client),
    quoteHire: client.quoteHire.bind(client),
    hireAgent: client.hireAgent.bind(client),
    listEscrows: client.listEscrows.bind(client),
    getEscrow: client.getEscrow.bind(client),
    claimEscrow: client.claimEscrow.bind(client),
    confirmEscrow: client.confirmEscrow.bind(client),
    disputeEscrow: client.disputeEscrow.bind(client),
    resolveEscrow: client.resolveEscrow.bind(client),
    timeoutEscrow: client.timeoutEscrow.bind(client),
    adjudicatorTimeoutEscrow: client.adjudicatorTimeoutEscrow.bind(client),
    getBadge: client.getBadge.bind(client),
    getBadgeImageURL: client.getBadgeImageURL.bind(client),
    listAgentBadges: client.listAgentBadges.bind(client),
    listBadges: client.listBadges.bind(client),
    issueAttestation: client.issueAttestation.bind(client),
    listAgentAttestations: client.listAgentAttestations.bind(client),
    revokeAttestation: client.revokeAttestation.bind(client),
    getTreasuryState: client.getTreasuryState.bind(client),
    proposeTreasury: client.proposeTreasury.bind(client),
    activateTreasury: client.activateTreasury.bind(client),
    walletSummary: client.walletSummary.bind(client),
    escrowTimeline: client.escrowTimeline.bind(client)
  };
}
function buildWalletNamespace(client) {
  return {
    getMarketplacePubkey: client.getMarketplacePubkey.bind(client),
    rotateMarketplacePubkey: client.rotateMarketplacePubkey.bind(client),
    signEIP712: client.signEIP712.bind(client),
    rotateSubkey: client.rotateSubkey.bind(client),
    requestSubkeyHandover: client.requestSubkeyHandover.bind(client),
    ackSubkeyHandover: client.ackSubkeyHandover.bind(client),
    subkeyStatus: client.subkeyStatus.bind(client)
  };
}

// src/pairwise.ts
import * as ed2 from "@noble/ed25519";
import { hkdf } from "@noble/hashes/hkdf";
import { sha256 as sha2562 } from "@noble/hashes/sha256";
var PAIRWISE_SALT = "ida-pairwise-v1";
function didKeyFromEd25519(publicKey) {
  const mc = new Uint8Array(2 + publicKey.length);
  mc[0] = 237;
  mc[1] = 1;
  mc.set(publicKey, 2);
  return `did:key:z${base58Encode(mc)}`;
}
async function derivePairwiseIdentity(masterSeed, relationship) {
  if (masterSeed.length < 32) throw new Error("master seed must be at least 32 bytes");
  if (!relationship) throw new Error("relationship is required");
  const enc = new TextEncoder();
  const seed = hkdf(sha2562, masterSeed, enc.encode(PAIRWISE_SALT), enc.encode(relationship), 32);
  const publicKey = await ed2.getPublicKeyAsync(seed);
  const did = didKeyFromEd25519(publicKey);
  return { did, keyId: `${did}#${did.slice("did:key:".length)}`, publicKey, seed };
}

// src/types.ts
var AutonomyLevel = /* @__PURE__ */ ((AutonomyLevel2) => {
  AutonomyLevel2[AutonomyLevel2["Intern"] = 0] = "Intern";
  AutonomyLevel2[AutonomyLevel2["Junior"] = 1] = "Junior";
  AutonomyLevel2[AutonomyLevel2["Senior"] = 2] = "Senior";
  AutonomyLevel2[AutonomyLevel2["Principal"] = 3] = "Principal";
  return AutonomyLevel2;
})(AutonomyLevel || {});

// src/index.ts
var IDAClient = class {
  constructor(config) {
    this.config = {
      ...config,
      apiUrl: config.apiUrl.replace(/\/$/, ""),
      timeout: config.timeout || 3e4
    };
    this.didOps = new DIDOperations(this.config);
    this.credentialOps = new CredentialOperations(this.config);
    this.agentOps = new AgentOperations(this.config);
    this.capabilityTokens = new CapabilityTokens(this.config);
    this.upi = new UPIOperations(this.config);
    this.zkpOps = new ZKPOperations(this.config);
    this.toolOps = new ToolOperations(this.config);
    this.policyOps = new PolicyOperations(this.config);
    this.auditOps = new AuditOperations(this.config);
    this.presentationOps = new PresentationOperations(this.config);
    this.marketplaceOps = new MarketplaceOperations(this.config);
    const ns = buildNamespaces(this);
    this.presentations = ns.presentations;
    this.tools = ns.tools;
    this.policies = ns.policies;
    this.audit = ns.audit;
    this.zkp = ns.zkp;
    this.marketplace = buildMarketplaceNamespace(this);
    this.wallet = buildWalletNamespace(this);
  }
  // --- DID Operations ---
  async createDID(options) {
    return this.didOps.createDID(options);
  }
  async resolveDID(did) {
    return this.didOps.resolveDID(did);
  }
  /** Resolve any DID method (did:adi, did:key, did:web, did:ethr) via universal resolver. */
  async resolveAny(did) {
    return this.didOps.resolveAny(did);
  }
  async updateDID(did, document, opts) {
    return this.didOps.updateDID(did, document, opts);
  }
  async deactivateDID(did, opts) {
    return this.didOps.deactivateDID(did, opts);
  }
  async rotateKey(did, keyType, currentKeyProof, opts) {
    return this.didOps.rotateKey(did, keyType, currentKeyProof, opts);
  }
  /** The message to sign with a current authentication key before {@link rotateKey}. */
  async getRotationChallenge(did) {
    return this.didOps.getRotationChallenge(did);
  }
  /** Every recorded version of a DID document, newest first. */
  async listDIDVersions(did) {
    return this.didOps.listDIDVersions(did);
  }
  /** Resolve a DID as it was at a version or a point in time. */
  async resolveDIDVersion(did, at) {
    return this.didOps.resolveDIDVersion(did, at);
  }
  // --- Delegate Operations (ERC-1056 inspired) ---
  async addDelegate(did, params, opts) {
    return this.didOps.addDelegate(did, params, opts);
  }
  async listDelegates(did) {
    return this.didOps.listDelegates(did);
  }
  async revokeDelegate(did, params, opts) {
    return this.didOps.revokeDelegate(did, params, opts);
  }
  // --- Attribute Operations (ERC-1056 inspired) ---
  async setAttribute(did, params, opts) {
    return this.didOps.setAttribute(did, params, opts);
  }
  async revokeAttribute(did, params, opts) {
    return this.didOps.revokeAttribute(did, params, opts);
  }
  // --- Verifiable Credential Operations ---
  /**
   * Issue a new Verifiable Credential.
   * @param params - Issuance parameters including issuer, subject, type, and claims.
   * @returns The issued Verifiable Credential with proof.
   */
  /**
   * Issue a credential signed by its issuer. Pass `opts.signer` (the
   * issuer's Ed25519 key, or a wallet signer); without one only the platform
   * DID can issue.
   */
  async issueCredential(params, opts) {
    return this.credentialOps.issueCredential(params, opts);
  }
  /** Prepare a credential for the issuer to sign. */
  async prepareCredential(params, opts) {
    return this.credentialOps.prepareCredential(params, opts);
  }
  /** Submit the issuer's proofValue for a prepared credential. */
  async completeCredential(credentialId, proofValue) {
    return this.credentialOps.completeCredential(credentialId, proofValue);
  }
  /**
   * Verify a Verifiable Credential's proof, status, and structure.
   * @param vc - The credential to verify.
   * @returns Verification result with checks and any errors.
   */
  async verifyCredential(vc) {
    return this.credentialOps.verifyCredential(vc);
  }
  /**
   * Revoke a Verifiable Credential.
   * @param credentialId - The ID of the credential to revoke.
   * @param reason - Reason for revocation.
   */
  async revokeCredential(credentialId, reason) {
    return this.credentialOps.revokeCredential(credentialId, reason);
  }
  async checkRevocationStatus(credentialId) {
    return this.credentialOps.checkRevocationStatus(credentialId);
  }
  // --- Presentation Operations ---
  /**
   * Create a Verifiable Presentation containing one or more credentials.
   * @param params - Presentation parameters including holder and credential IDs.
   * @returns The created Verifiable Presentation with proof.
   */
  /** Prepare an unsigned presentation for the holder to sign. */
  async createPresentation(params) {
    return this.credentialOps.createPresentation(params);
  }
  /** Prepare and sign a presentation with the holder's key. */
  async createSignedPresentation(params, signer) {
    return this.credentialOps.createSignedPresentation(params, signer);
  }
  /**
   * Verify a Verifiable Presentation.
   * @param vp - The presentation to verify.
   * @returns Verification result.
   */
  async verifyPresentation(vp) {
    return this.credentialOps.verifyPresentation(vp);
  }
  // --- Phase 7-C: DIF Presentation Exchange v2 ---
  /**
   * Create a verifier-owned Presentation Definition.
   * Idempotent on `Idempotency-Key` for 24h.
   */
  async createPresentationDefinition(params, opts) {
    return this.presentationOps.createPresentationDefinition(params, opts);
  }
  /** Cursor-paginated list of PDs the caller owns. */
  async listPresentationDefinitions(opts) {
    return this.presentationOps.listPresentationDefinitions(opts);
  }
  /** Owner-scoped fetch of a single PD with full body. */
  async getPresentationDefinition(id) {
    return this.presentationOps.getPresentationDefinition(id);
  }
  /**
   * Public, no-auth holder fetch by id. Strips operator metadata; returns a
   * freshly-minted `challenge` and `domain`.
   */
  async getPublicPresentationDefinition(id) {
    return this.presentationOps.getPublicPresentationDefinition(id);
  }
  /**
   * Update a PD with optimistic CAS. `ifVersion` must equal the current
   * version on the row; mismatch returns `409 PD_VERSION_CONFLICT`.
   */
  async updatePresentationDefinition(id, params, ifVersion) {
    return this.presentationOps.updatePresentationDefinition(id, params, ifVersion);
  }
  /** Soft-delete (revoke) a PD. */
  async deletePresentationDefinition(id, reason) {
    return this.presentationOps.deletePresentationDefinition(id, reason);
  }
  /**
   * Evaluate a Verifiable Presentation against a Presentation Definition.
   * Returns per-input-descriptor outcomes; never short-circuits.
   */
  async evaluatePresentation(params) {
    return this.presentationOps.evaluatePresentation(params);
  }
  // --- Agent Operations ---
  /**
   * Register a new AI agent with a decentralized identity.
   * @param params - Agent registration parameters.
   * @returns The registered agent DID and metadata.
   */
  async registerAgent(params, opts) {
    return this.agentOps.registerAgent(params, opts);
  }
  /**
   * Get agent details by DID.
   * @param agentDid - The agent's DID.
   * @returns Agent details including trust score and capabilities.
   */
  async getAgent(agentDid) {
    return this.agentOps.getAgent(agentDid);
  }
  /** @deprecated Use {@link issueDelegation}: delegations must be signed by the delegator. */
  async issueAgentDelegation(params, signer) {
    return this.agentOps.issueAgentDelegation(params, signer);
  }
  /** Prepare a signed delegation (AgentDelegationCredential) for the delegator to sign. */
  async prepareDelegation(params) {
    return this.agentOps.prepareDelegation(params);
  }
  /** Submit the delegator's proofValue for a prepared delegation. */
  async completeDelegation(credentialId, proofValue) {
    return this.agentOps.completeDelegation(credentialId, proofValue);
  }
  /** Prepare, sign and submit a delegation. */
  async issueDelegation(params, signer) {
    return this.agentOps.issueDelegation(params, signer);
  }
  /** Verify a delegation's whole chain, optionally for an action. */
  async verifyDelegation(delegationId, action) {
    return this.agentOps.verifyDelegation(delegationId, action);
  }
  /** Revoke a delegation and everything attenuated from it. */
  async revokeDelegation(delegationId, reason) {
    return this.agentOps.revokeDelegation(delegationId, reason);
  }
  /**
   * Verify a complete delegation chain from root delegator to leaf agent.
   *
   * @deprecated Use `IDAClient.verifyDelegationChainSteps()` instead. The
   * Phase-7-A server returns the new `VerifyChainResult` shape; the legacy
   * `ChainVerificationResult` is preserved for backwards compatibility but
   * no longer matches the wire response.
   * @param chain - The delegation chain to verify.
   * @returns Detailed verification result with per-hop status.
   */
  async verifyDelegationChain(chain) {
    return this.agentOps.verifyDelegationChain(chain);
  }
  /**
   * Discover agents by capability, trust score, or other criteria.
   * @param query - Search query parameters.
   * @returns Paginated list of matching agent cards.
   */
  /** List the agents you operate, optionally narrowed by label. */
  async listAgents(query) {
    return this.agentOps.listAgents(query);
  }
  async discoverAgents(query) {
    return this.agentOps.discoverAgents(query);
  }
  /**
   * Get the A2A-compatible Agent Card for an agent.
   * @param agentDid - The agent's DID.
   * @returns The agent card in A2A format.
   */
  async getAgentCard(agentDid) {
    return this.agentOps.getAgentCard(agentDid);
  }
  /**
   * Decommission an AI agent (permanent deactivation).
   * This triggers cascading revocation of all delegations and credentials.
   * @param agentDid - The agent's DID.
   */
  async decommissionAgent(agentDid, opts) {
    return this.agentOps.decommissionAgent(agentDid, opts);
  }
  /**
   * Suspend an agent (reversible). While suspended it cannot mint or redeem
   * IBCTs or issue or receive delegations. A reason is required.
   */
  async suspendAgent(agentDid, reason, txHash) {
    return this.agentOps.suspendAgent(agentDid, reason, txHash);
  }
  /** Lift an agent's suspension. */
  async reactivateAgent(agentDid, txHash) {
    return this.agentOps.reactivateAgent(agentDid, txHash);
  }
  /**
   * Update an agent's metadata / card.
   * Dual-mode: pass `opts.signer` to submit AgentTrustRegistry.updateAgentCard()
   * on-chain (signer must be the operator); omit for the legacy API path.
   */
  async updateAgent(agentDid, updates, opts) {
    return this.agentOps.updateAgent(agentDid, updates, opts);
  }
  /**
   * Set an agent's autonomy level on-chain (self-sovereign only — the signer must
   * be the agent operator). No legacy API equivalent.
   */
  async setAutonomyLevel(agentDid, level, opts) {
    return this.agentOps.setAutonomyLevel(agentDid, level, opts);
  }
  // --- AgentSight Provisioning Pipeline ---
  /**
   * Provision an agent through the AgentSight pipeline.
   * Mints a DID on ADI blockchain, issues an AgentIdentityCredential, and returns
   * a one-time IBCT signing key. Use this for platform integrations (e.g. AgentSight).
   */
  async provisionAgent(params) {
    return this.agentOps.provisionAgent(params);
  }
  /**
   * Submit a trust signal for an agent to update its score and autonomy level.
   * Supported types: task_completed, task_failed, policy_violation, manual_attestation,
   * delegation_granted, delegation_revoked, ibct_redeemed, ibct_abused, compliance_passed.
   */
  async recordTrustSignal(agentDid, signal, opts) {
    return this.agentOps.recordTrustSignal(agentDid, signal, opts);
  }
  /**
   * Register a webhook to receive agent lifecycle events (agent.provisioned, agent.revoked, agent.trust_updated).
   * The secret is used to verify HMAC-SHA256 signatures on delivered payloads.
   */
  async registerWebhook(params) {
    return this.agentOps.registerWebhook(params);
  }
  /**
   * List all webhook subscriptions for an owner DID.
   */
  async listWebhooks(ownerDid) {
    return this.agentOps.listWebhooks(ownerDid);
  }
  /**
   * Delete a webhook subscription.
   */
  async deleteWebhook(subscriptionId) {
    return this.agentOps.deleteWebhook(subscriptionId);
  }
  /**
   * Get delivery history for a webhook subscription.
   */
  async getWebhookDeliveries(subscriptionId, limit) {
    return this.agentOps.getWebhookDeliveries(subscriptionId, limit);
  }
  // --- Zero-Knowledge Proof Operations ---
  /**
   * Generate a fresh cryptographic challenge for ZK proof generation.
   * @returns Challenge string and expiration time.
   */
  async generateZKChallenge() {
    return this.zkpOps.generateChallenge();
  }
  /**
   * Generate a ZK proof for credential predicates without revealing actual values.
   * Supports comparison (gt, gte, lt, lte, eq), range, and membership predicates.
   * Compatible with zkSync Airbender proving system on ADI blockchain.
   * @param params - Proof generation parameters including predicates and credential subject.
   * @returns The generated proof with optional on-chain hash.
   */
  async generateZKProof(params) {
    return this.zkpOps.generateZKProof(params);
  }
  /**
   * Verify a ZK proof off-chain by resolving the prover's DID and checking the proof.
   * @param proof - The ZK proof to verify.
   * @param proverDid - The DID of the prover.
   * @returns Detailed verification result with per-predicate status.
   */
  async verifyZKProof(proof, proverDid) {
    return this.zkpOps.verifyZKProof(proof, proverDid);
  }
  /**
   * Retrieve a stored ZK proof by ID.
   * @param proofId - The proof ID (e.g., 'urn:uuid:...').
   * @returns The stored ZK proof.
   */
  async getZKProof(proofId) {
    return this.zkpOps.getZKProof(proofId);
  }
  // --- Phase-7-A: ZK Anchoring & Delegated Proofs ---
  /**
   * Anchor a ZK proof on the on-chain `ZKProofVerifier` registry.
   *
   * @param proofId - The proof ID returned by `generateZKProof`.
   * @param opts - Optional `idempotencyKey` (auto-generated when omitted) and `syncWaitMs`.
   * @returns The created anchor record. Status starts at `pending`; poll `getZKAnchor` for confirmation.
   */
  async anchorZKProof(proofId, opts) {
    return this.zkpOps.anchorZKProof(proofId, opts);
  }
  /**
   * Read the current anchor record for a proof.
   *
   * @param proofId - The proof ID.
   * @returns The anchor record.
   */
  async getZKAnchor(proofId) {
    return this.zkpOps.getZKAnchor(proofId);
  }
  /**
   * List anchor records, paginated.
   *
   * @param params - Filters: `did`, `holderDid`, `since`, `status`, `kind`, `limit`, `offset`.
   */
  async listZKAnchors(params) {
    return this.zkpOps.listZKAnchors(params);
  }
  /**
   * Generate a delegated ZK proof — an agent proves a predicate on the
   * holder's behalf, witnessed by an IBCT.
   *
   * @param params - Holder/agent DIDs, IBCT, predicate, optional anchor flag.
   */
  async generateDelegatedZKProof(params) {
    return this.zkpOps.generateDelegatedZKProof(params);
  }
  /**
   * Verify a multi-hop delegation chain (root → leaf).
   *
   * @param chain - Ordered chain of delegation steps.
   * @param opts - Optional leaf agent DID and action scope to enforce.
   */
  async verifyDelegationChainSteps(chain, opts) {
    return this.zkpOps.verifyDelegationChain(chain, opts);
  }
  /**
   * Revoke a previously-anchored ZK proof on chain.
   */
  async revokeZKProof(proofId, opts) {
    return this.zkpOps.revokeZKProof(proofId, opts);
  }
  // --- Phase-7-E: MCP Tool Registry ---
  /** List MCP tools registered against an agent. */
  async listTools(agentDid, opts) {
    return this.toolOps.list(agentDid, opts);
  }
  /** Get one tool by id (returns even when soft-revoked). */
  async getTool(agentDid, toolId) {
    return this.toolOps.get(agentDid, toolId);
  }
  /** Register a new MCP tool against the agent. Idempotent via `Idempotency-Key`. */
  async registerTool(agentDid, params, opts) {
    return this.toolOps.register(agentDid, params, opts);
  }
  /**
   * Update a tool. `rowVersion` is the optimistic-concurrency token from the
   * latest GET; on mismatch the server returns `409 STALE_VERSION`.
   */
  async updateTool(agentDid, toolId, params, rowVersion) {
    return this.toolOps.update(agentDid, toolId, params, rowVersion);
  }
  /** Soft-revoke a tool. The row stays for audit; DID Doc `service[]` is rebuilt. */
  async revokeTool(agentDid, toolId, reason) {
    return this.toolOps.revoke(agentDid, toolId, reason);
  }
  // --- Phase-7-E: Audit Trail Explorer ---
  /** Cursor-paginated audit-log history. */
  async getAuditLog(agentDid, params) {
    return this.auditOps.getAuditLog(agentDid, params);
  }
  /**
   * Subscribe to the per-agent audit SSE stream. Internally mints a 30s
   * ticket via `POST /audit-stream/ticket`, then opens an `EventSource`.
   * Auto-reconnects with exponential backoff (max 30s, default 5 attempts);
   * resumes from the last seen event id.
   *
   * @returns A handle whose `close()` shuts down the stream.
   */
  subscribeAuditStream(agentDid, opts) {
    return this.auditOps.subscribeAuditStream(agentDid, opts);
  }
  // --- Phase-7-E: IBCT Policies (operator-scoped) ---
  /** List IBCT policies the caller owns (or all, for admins). */
  async listPolicies(opts) {
    return this.policyOps.list(opts);
  }
  async getPolicy(policyId) {
    return this.policyOps.get(policyId);
  }
  /** Upsert a policy: POST when `params.id` is absent, PUT (with `ifVersion`) otherwise. */
  async upsertPolicy(params) {
    return this.policyOps.upsert(params);
  }
  async deletePolicy(policyId) {
    return this.policyOps.delete(policyId);
  }
  // --- Phase-7-E: IBCT mint ---
  /** Mint a public IBCT scoped to an agent. */
  async mintIBCT(agentDid, params, opts) {
    return this.policyOps.mintIBCT(agentDid, params, opts);
  }
  /**
   * Redeem an IBCT before acting on it. Each token is accepted once; the
   * server also checks signature, expiry, action, redeemer and agent status.
   */
  async redeemIBCT(ibct, action) {
    return this.policyOps.redeemIBCT(ibct, action);
  }
  /** Fetch the public key that verifies IBCTs this server mints. */
  async getIBCTSigningKey() {
    return this.policyOps.getIBCTSigningKey();
  }
  // ===================================================================
  // Phase 7-B — Marketplace v2 + Tokenised Reputation
  //
  // 29-method surface mirroring `02_api_spec.md §3-§10`. Namespaced
  // mirrors live on `client.marketplace.*`; both styles are bind-equivalent.
  // ===================================================================
  /** Cached, no-auth read of chain-bound marketplace constants. */
  async getMarketplaceConfig() {
    return this.marketplaceOps.getConfig();
  }
  // --- Listings ---
  async listListings(params) {
    return this.marketplaceOps.listListings(params);
  }
  async getListing(id) {
    return this.marketplaceOps.getListing(id);
  }
  async createListing(params, opts) {
    return this.marketplaceOps.createListing(params, opts);
  }
  async updateListing(id, params) {
    return this.marketplaceOps.updateListing(id, params);
  }
  async deleteListing(id) {
    return this.marketplaceOps.deleteListing(id);
  }
  async pauseListing(id, reason) {
    return this.marketplaceOps.pauseListing(id, reason);
  }
  async resumeListing(id) {
    return this.marketplaceOps.resumeListing(id);
  }
  // --- Operator self-service ---
  async listMyListings(params) {
    return this.marketplaceOps.listMyListings(params);
  }
  async getMyEarnings(params) {
    return this.marketplaceOps.getMyEarnings(params);
  }
  // --- Quote + Hire ---
  /** Compute price + IBCT scope + signing nonce. Returns the exact `HireOrder` to sign. */
  async quoteHire(listingId, params, opts) {
    return this.marketplaceOps.quoteHire(listingId, params, opts);
  }
  async hireAgent(listingId, signed, opts) {
    return this.marketplaceOps.hireAgent(listingId, signed, opts);
  }
  // --- Escrow lifecycle ---
  async listEscrows(params) {
    return this.marketplaceOps.listEscrows(params);
  }
  async getEscrow(escrowId) {
    return this.marketplaceOps.getEscrow(escrowId);
  }
  async claimEscrow(escrowId, signed) {
    return this.marketplaceOps.claimEscrow(escrowId, signed);
  }
  async confirmEscrow(escrowId, signed) {
    return this.marketplaceOps.confirmEscrow(escrowId, signed);
  }
  async disputeEscrow(escrowId, signed, _reason) {
    return this.marketplaceOps.disputeEscrow(escrowId, signed);
  }
  async resolveEscrow(escrowId, splitOrder, adjudicatorSignatures) {
    return this.marketplaceOps.resolveEscrow(escrowId, splitOrder, adjudicatorSignatures);
  }
  async timeoutEscrow(escrowId) {
    return this.marketplaceOps.timeoutEscrow(escrowId);
  }
  async adjudicatorTimeoutEscrow(escrowId) {
    return this.marketplaceOps.adjudicatorTimeoutEscrow(escrowId);
  }
  // --- Reputation badges ---
  async getBadge(tokenId) {
    return this.marketplaceOps.getBadge(tokenId);
  }
  /** Convenience accessor — returns the SVG image URL. */
  getBadgeImageURL(tokenId) {
    return this.marketplaceOps.getBadgeImageURL(tokenId);
  }
  async listAgentBadges(agentDid, params) {
    return this.marketplaceOps.listAgentBadges(agentDid, params);
  }
  async listBadges(params) {
    return this.marketplaceOps.listBadges(params);
  }
  // --- Attestations ---
  async issueAttestation(agentDid, params, opts) {
    return this.marketplaceOps.issueAttestation(agentDid, params, opts);
  }
  async listAgentAttestations(agentDid, params) {
    return this.marketplaceOps.listAgentAttestations(agentDid, params);
  }
  async revokeAttestation(agentDid, attestationId, reason) {
    return this.marketplaceOps.revokeAttestation(agentDid, attestationId, reason);
  }
  // --- Treasury admin ---
  async getTreasuryState() {
    return this.marketplaceOps.getTreasuryState();
  }
  async proposeTreasury(params) {
    return this.marketplaceOps.proposeTreasury(params);
  }
  async activateTreasury() {
    return this.marketplaceOps.activateTreasury();
  }
  // --- Wallet sub-key (EIP-712 signing path) ---
  async getMarketplacePubkey() {
    return this.marketplaceOps.getMarketplacePubkey();
  }
  async rotateMarketplacePubkey() {
    return this.marketplaceOps.rotateMarketplacePubkey();
  }
  /**
   * Server-mediated EIP-712 sign — `typedData` MUST be the output of one of
   * the `buildXxxTypedData` helpers from `@infinia/ida-sdk/marketplace`.
   */
  async signEIP712(typedData) {
    return this.marketplaceOps.signEIP712(typedData);
  }
  // ===================================================================
  // SDK 1.5.0 — Phase 7-D mobile wallet marketplace
  // ===================================================================
  /** Aggregate counters for the wallet's home + marketplace tabs. */
  async walletSummary(params) {
    return this.marketplaceOps.walletSummary(params);
  }
  /** Pre-shaped on-chain event timeline (last N) for one escrow. */
  async escrowTimeline(params) {
    return this.marketplaceOps.escrowTimeline(params);
  }
  /** Wallet-side sub-key rotation — wallet generates the new keypair locally. */
  async rotateSubkey(input, opts) {
    return this.marketplaceOps.rotateSubkey(input, opts);
  }
  /** Step 1 of the §4.2 handover — server returns an X25519+XChaCha20 envelope. */
  async requestSubkeyHandover(input, opts) {
    return this.marketplaceOps.requestSubkeyHandover(input, opts);
  }
  /** Step 2 of the §4.2 handover — server wipes its priv on success. */
  async ackSubkeyHandover(input, opts) {
    return this.marketplaceOps.ackSubkeyHandover(input, opts);
  }
  /** Read-only state of the caller's subkey lifecycle. */
  async subkeyStatus() {
    return this.marketplaceOps.subkeyStatus();
  }
};
export {
  AGENT_STATUS,
  AGENT_TRUST_REGISTRY_ABI,
  AUTONOMY_LEVEL,
  AutonomyLevel,
  CHAINS,
  CRYPTOSUITE_EDDSA_JCS,
  CRYPTOSUITE_EIP191_JCS,
  CapabilityTokens,
  DATA_INTEGRITY_PROOF,
  DID_REGISTRY_ABI,
  HOME_CHAIN_ID,
  IDAClient,
  PAIRWISE_SALT,
  REVOCATION_REGISTRY_ABI,
  SCHEMA_REGISTRY_ABI,
  TYPED_DATA_FIELDS,
  UPIOperations,
  ZK_PROOF_VERIFIER_ABI,
  addDelegateOnChain,
  attachProof,
  base58Decode,
  base58Encode,
  base64UrlDecode,
  base64UrlEncode,
  batchRevokeOnChain,
  buildClaimReceiptTypedData,
  buildConfirmReceiptTypedData,
  buildDID,
  buildDisputeReceiptTypedData,
  buildHireOrderTypedData,
  buildResolveOrderTypedData,
  buildWalletDIDDocument,
  bytesToHex,
  canonicalize,
  chainForDID,
  computeDescriptorDiff,
  computeDiff,
  confirmSponsorshipOnChain,
  createDIDOnChain,
  createSchemaOnChain,
  deactivateDIDOnChain,
  decommissionAgentOnChain,
  derivePairwiseIdentity,
  didKeyFromEd25519,
  domainFromConfig,
  ed25519Sign,
  ed25519Verify,
  encodeDocument,
  encodeEd25519ProofValue,
  encodeType,
  evalPath,
  generateEd25519KeyPair,
  generateNonce,
  generateSecp256k1KeyPair,
  getChain,
  getSponsorOnChain,
  hashData,
  hashDomain,
  hashStruct,
  hashTypedData,
  hashTypedDataHex,
  hexToBytes,
  isRevokedOnChain,
  listChains,
  lookupDIDOnChain,
  mintCapabilityToken,
  parseDID,
  proofConfig,
  registerAgentOnChain,
  requireEnabledChain,
  revokeAttributeOnChain,
  revokeCredentialOnChain,
  revokeDelegateOnChain,
  revokeProofOnChain,
  revokeSponsorshipOnChain,
  rotateKeyOnChain,
  secp256k1Sign,
  secp256k1Verify,
  setAttributeOnChain,
  setAutonomyLevelOnChain,
  setEventSourceFactory,
  sha256,
  signDocumentEd25519,
  signHashDataEd25519,
  signPreparedCredential,
  signPreparedPresentation,
  submitProofOnChain,
  submitTrustSignalOnChain,
  toBytes32,
  updateAgentCardOnChain,
  updateAgentStatusOnChain,
  updateDIDDocumentOnChain,
  updateSchemaOnChain,
  verifyEd25519Proof,
  verifyProofOnChain
};
